<template>
  <div class="position-relative">
    <div v-if="hideFullPageSpinner && isLoadingDetails" class="inner-spinner">
      <BSpinner variant="primary" />
    </div>
    <div v-if="!user && loadError" id="user_details_load_error" class="card">
      <b-card-body>
        <b-alert show variant="danger" class="mb-1">
          <div class="p-1">
            <h4 class="alert-heading">User details unavailable</h4>
            <p :class="offerActivityLinks.length ? 'mb-1' : 'mb-0'">{{ loadError }}</p>
            <p v-if="offerActivityLinks.length" class="mb-0">
              Offer activity for this user is still available on the
              <template v-for="(link, index) in offerActivityLinks">
                <b-link :key="link.name" :to="{ name: link.name, query: { filterUserId: userId } }">{{ link.label }}</b-link>
                <span :key="`${link.name}-separator`">{{ index === offerActivityLinks.length - 1 ? ' ' : ', ' }}</span>
              </template>
              page<span v-if="offerActivityLinks.length > 1">s</span>.
            </p>
          </div>
        </b-alert>
        <b-button variant="primary" @click="getUserDetails">Try again</b-button>
      </b-card-body>
    </div>
    <div v-if="user" id="user_details_page">
      <div class="card">
        <quick-links v-if="shownAsModal" class="box-shadowed" :useActiveLink="false" />
        <b-card-header>
          <b-alert v-if="staffReviewer" :show="$store.state.auth.AppActiveAdmin.id !== staffReviewer.id" variant="warning" class="p-1 mb-2 border border-warning">
            <div class="d-flex text-dark align-items-center">
              <UserBadgeName :name="staffReviewer.name" :color="staffReviewer.hex_color" />
              <div class="text-bold pl-25">is looking at this user</div>
            </div>
          </b-alert>
          <div
              style="
            display: flex;
            justify-content: space-between;
            width: 100%;
            gap: 20px;
          "
          >
            <div>
              <h3>User Details</h3>
              <table class="table b-table table-striped table-hover">
                <tbody>
                <tr>
                  <td>User Id</td>
                  <td>{{ user.id }}</td>
                </tr>
                <tr>
                  <td>App</td>
                  <td class="d-flex">
                    <AppElement :id="user.app_id" :name="user.app_name" />
                  </td>
                </tr>
                <tr>
                  <td>App Links</td>
                  <td>
                    <div v-if="hasAppLinks" class="d-flex flex-wrap" style="gap: 5px">
                      <b-badge
                          v-if="safeAppUrl(user.app_url)"
                          variant="light-primary"
                          :href="safeAppUrl(user.app_url)"
                          target="_blank"
                          rel="noopener"
                      >Webpage</b-badge>
                      <b-badge
                          v-if="safeAppUrl(user.app_apple_app_store_url)"
                          variant="light-primary"
                          :href="safeAppUrl(user.app_apple_app_store_url)"
                          target="_blank"
                          rel="noopener"
                      >Apple App Store</b-badge>
                      <b-badge
                          v-if="safeAppUrl(user.app_google_play_url)"
                          variant="light-primary"
                          :href="safeAppUrl(user.app_google_play_url)"
                          target="_blank"
                          rel="noopener"
                      >Google Play</b-badge>
                    </div>
                    <span v-else class="text-muted">-</span>
                  </td>
                </tr>
                <tr>
                  <td>App Support</td>
                  <td>
                    <div v-if="hasAppSupport" class="d-flex flex-wrap align-items-center" style="gap: 10px">
                      <a v-if="safeAppText(user.app_support_email)" :href="`mailto:${safeAppText(user.app_support_email)}`">
                        {{ safeAppText(user.app_support_email) }}
                      </a>
                      <span v-if="safeAppText(user.app_support_phone)">{{ safeAppText(user.app_support_phone) }}</span>
                      <a
                          v-if="safeAppUrl(user.app_support_url)"
                          :href="safeAppUrl(user.app_support_url)"
                          target="_blank"
                          rel="noopener"
                      >Support page</a>
                    </div>
                    <span v-else class="text-muted">-</span>
                  </td>
                </tr>
                <tr v-if="user.app_comments && user.app_comments.trim()">
                  <td>App comments</td>
                  <td style="white-space: pre-line" class="text-danger">{{ user.app_comments }}</td>
                </tr>
                <tr v-if="user.is_po">
                  <td>PO User ID</td>
                  <td class="d-flex text-nowrap align-items-center" style="gap: 5px">
                    <b-img
                        :src="primeOpinionLogo"
                        alt="prime-opinion-logo"
                        class="mr-50"
                        width="20px"
                    ></b-img>
                    <POUserElement :id="user.po_user_id" :additionalText="user.po_user_deleted ? '(Deleted)' : ''" target="_blank" />
                  </td>
                </tr>
                <tr v-if="user.is_fs">
                  <td>FS User ID</td>
                  <td class="d-flex text-nowrap align-items-center" style="gap: 5px">
                    <b-img
                        :src="fiveSurveysLogo"
                        alt="five-surveys-logo"
                        class="mr-50"
                        width="25px"
                    ></b-img>
                    <FSUserElement :id="user.fs_user_id" :additionalText="user.fs_user_deleted ? '(Deleted)' : ''" target="_blank" />
                  </td>
                </tr>
                <tr v-if="user.is_hc">
                  <td>HC User ID</td>
                  <td class="d-flex text-nowrap align-items-center" style="gap: 5px">
                    <b-img
                        :src="heyCashLogo"
                        alt="five-surveys-logo"
                        class="mr-50"
                        width="25px"
                    ></b-img>
                    <HCUserElement :id="user.hc_user_id" :additionalText="user.hc_user_deleted ? '(Deleted)' : ''" target="_blank" />
                  </td>
                </tr>
                <tr v-if="user.is_ts">
                  <td>TS User ID</td>
                  <td class="d-flex text-nowrap align-items-center" style="gap: 5px">
                    <b-img
                        :src="topSurveysLogo"
                        alt="top-surveys-logo"
                        class="mr-50"
                        width="25px"
                    ></b-img>
                    <TSUserElement :id="user.ts_user_id" :additionalText="user.ts_user_deleted ? '(Deleted)' : ''" target="_blank" />
                  </td>
                </tr>
                <tr v-if="user.is_es">
                  <td>ES User ID</td>
                  <td class="d-flex text-nowrap align-items-center" style="gap: 5px">
                    <b-img
                        :src="earnStarLogo"
                        alt="earn-star-logo"
                        class="mr-50"
                        width="25px"
                    ></b-img>
                    <ESUserElement :id="user.es_user_id" :additionalText="user.es_user_deleted ? '(Deleted)' : ''" target="_blank" />
                  </td>
                </tr>
                <tr v-if="user.is_pt">
                  <td>PT User ID</td>
                  <td class="d-flex text-nowrap align-items-center" style="gap: 5px">
                    <b-img
                        :src="paidTesterLogo"
                        alt="paid-tester-logo"
                        class="mr-50"
                        width="25px"
                    ></b-img>
                    <PTUserElement :id="user.pt_user_id" :additionalText="user.pt_user_deleted ? '(Deleted)' : ''" target="_blank" />
                  </td>
                </tr>
                <tr>
                  <td>Platform</td>
                  <td class="d-flex">
                    <span v-if="platformLastHundredClicks" id="platform-device-icon-wrapper" class="d-inline-block">
                      <PlatformDeviceIcon
                          :platform="platformLastHundredClicks"
                          style="pointer-events: none"
                      />
                      <b-tooltip target="platform-device-icon-wrapper" triggers="hover" placement="bottomright">
                        <div class="text-left">
                          Last 100 clicks: <strong>{{ platformLastHundredClicks }}</strong><br>
                          Profile Device: <strong>{{ user.device }}</strong>
                        </div>
                      </b-tooltip>
                    </span>
                    <span v-else></span>
                  </td>
                </tr>
                <tr>
                  <td>User Hash</td>
                  <td>{{ user.hash }}</td>
                </tr>
                <tr>
                  <td>External Id</td>
                  <td v-if="showMinimalizeExternalId">
                    {{ user.external_user_id.substr(0, 50) }}<span v-if="user.external_user_id.length > 50" class="text-primary" @click="showMinimalizeExternalId = false">...</span>
                  </td>
                  <td v-else>{{ user.external_user_id }}</td>
                </tr>
                <tr>
                  <td>External Source</td>
                  <td>{{ user.external_source }}</td>
                </tr>
                <tr>
                  <td>E-Mail</td>
                  <td class="text-nowrap">
                    <div class="d-flex align-items-center">
                      <template v-if="emailDetails.name && emailDetails.domain">
                        {{ emailDetails.name }}@<b-link @click="showFraudEmailDomains(emailDetails.domain)">{{ emailDetails.domain }}</b-link>
                      </template>
                      <b-badge v-if="user.is_deletion_pending" variant="warning" style="font-size: 11px">
                        Deletion pending - {{ user.deletion_scheduled_at | dotDateTime }}
                      </b-badge>
                      <b-badge v-else-if="user.is_anonymized" variant="danger" style="font-size: 11px">Deleted</b-badge>
                      <b-badge v-if="isHighRiskEmailDomain" variant="danger" style="font-size: 11px">High-Risk Email</b-badge>
                      <b-button
                        v-if="canUpdateEmail"
                        size="sm"
                        variant="outline-primary"
                        class="ml-50"
                        @click="openEmailModal"
                      >
                        {{ emailActionLabel }}
                      </b-button>
                      <!-- Show a clickable badge for breaches if there is real data -->
                    </div>

                    <div v-if="emailDetails.name && emailDetails.domain" class="d-flex align-items-center mt-50" style="gap: 5px">
                      <b-button
                          v-if="emailBreaches"
                          :variant="breachesButtonColor"
                          size="sm"
                          class="cursor-pointer"
                          @click="handleClickBreaches"
                      >
                        {{ breachesButtonText }}
                      </b-button>

                      <!-- Show a clickable badge for connected accounts if sum > 0 -->
                      <b-button
                          v-if="connectedAccounts"
                          :variant="connectedButtonColor"
                          size="sm"
                          class="cursor-pointer"
                          @click="handleClickAccountsConnected"
                      >
                        {{ connectedBadgeText }}
                      </b-button>

                      <!-- Show 'X Years old' if the backend returned a numeric age -->
                      <b-button
                          v-if="emailBreaches"
                          size="sm"
                          :variant="emailAgeColorClass"
                      >
                        {{ emailAgeText }}
                      </b-button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>Country</td>
                  <td>
                    <CountryElement
                        :code="user.country"
                        :countries="countries"
                        :item="user"
                        :showHighRiskIndicator="isUserCountryHighRisk"
                        :name="user.country_name"
                        :url="`/api/user/${user.id}/country`"
                        :withUpdateCountry="true"
                        @updated="updateCountry(user, $event)"
                    />
                  </td>
                </tr>
                <tr>
                  <td>Languages</td>
                  <td @click="showEditLanguage = true">
                    <div v-show="!showEditLanguage">
                      {{
                        user.languages
                            .map((language) => language.language)
                            .join(", ")
                      }}
                    </div>
                    <div
                        v-if="showEditLanguage"
                        class="row d-flex align-items-center"
                    >
                      <v-select
                          v-model="user.languages"
                          :options="user.available_languages"
                          class="col-10"
                          label="language"
                          multiple
                      />
                      <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          class="btn-icon"
                          title="Save"
                          variant="success"
                          @click="updateUserLanguages"
                      >
                        <feather-icon icon="CheckIcon"/>
                      </b-button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>Interface Languages</td>
                  <td @click="showEditInterfaceLanguage = true">
                    <div v-show="!showEditInterfaceLanguage">
                      {{ languageCodes[user.interface_language] }}
                    </div>
                    <div
                        v-if="showEditInterfaceLanguage"
                        class="row d-flex align-items-center"
                    >
                      <v-select
                          v-model="userInterfaceLanguage"
                          :options="Object.entries(languageCodes).map(([code, name]) => ({ code, name }))"
                          class="col-10"
                          label="name"
                          value="code"
                          placeholder="Select a language"
                      />
                      <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          class="btn-icon"
                          title="Save"
                          variant="success"
                          @click="updateUserInterfaceLanguage"
                      >
                        <feather-icon icon="CheckIcon"/>
                      </b-button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>Status</td>
                  <td style="line-height: 20px; position: relative">
                    <div class="row">
                      <div class="col-11">
                        <template v-if="user.status === 1">
                          <b-badge style="font-size: 11px" variant="success"
                          >Active
                          </b-badge>
                          <template v-if="user.protected_status > 0">
                            <br/>
                            <span>
                              <b>Protect: </b>
                              <span v-if="user.protected_status === 2"
                              >until
                                {{
                                  user.protected_until | dotDateTimeShort
                                }}
                                (<span
                                    :class="
                                    user.status_left_time <= 2
                                      ? 'text-warning'
                                      : ''
                                  "
                                >{{ user.status_left_time }}</span
                                >
                                days left)</span
                              >
                              <span v-if="user.protected_status === 1"
                              >infinite</span
                              >
                              <span v-if="user.protected_status === 3">
                                <span v-if="user.protected_completes > 0">
                                  {{ `For ${user.protected_completes} completes` }}
                                  (<span :class="user.completes_left <= 5 ? 'text-warning' : ''">
                                    {{ user.completes_left }} left
                                  </span>)
                                </span>

                                <span
                                    v-if="user.protected_offers_payout > 0"
                                    :style="user.protected_completes > 0 ? 'display: block;' : ''"
                                >
                                  <b v-if="user.protected_completes > 0">Protect: </b>
                                  {{ `For $${user.protected_offers_payout} Offer Payout` }}
                                  (<span :class="user.offer_payout_left <= 5 ? 'text-warning' : ''">
                                    ${{ user.offer_payout_left }} left
                                  </span>)
                                </span>
                              </span>
                            </span>
                          </template>
                        </template>
                        <template v-if="user.status === 2">
                          <b-badge
                              style="font-size: 11px"
                              variant="danger"
                              @click="showProtectedStatusModal()"
                          >Banned
                          </b-badge>
                          <br/>
                          <b>Until: </b>
                          <span v-if="user.banned_until">
                            {{ user.banned_until | dotDateTime }}</span
                          >
                          <span v-else>Infinite</span>
                        </template>
                        <template v-if="user.status === 3">
                          <b-badge
                              class="user-status-3"
                              style="font-size: 11px"
                              @click="showProtectedStatusModal()"
                          >Shadow Banned
                          </b-badge>
                          <br/>
                          <b>Until: </b>
                          <span v-if="user.banned_until">
                            {{ user.banned_until | dotDateTime }}</span
                          >
                          <span v-else>Infinite</span>
                        </template>
                        <template v-if="user.status === 4">
                          <b-badge
                              style="font-size: 11px"
                              variant="warning"
                              @click="showProtectedStatusModal()"
                          >Review
                          </b-badge>
                        </template>
                        <template v-if="user.status === 5">
                          <b-badge
                              class="user-status-5"
                              style="font-size: 11px"
                              @click="showProtectedStatusModal()"
                          >Shadow Review
                          </b-badge>
                        </template>
                        <template v-if="user.status === 6">
                          <b-badge
                              class="user-status-6"
                              style="font-size: 11px"
                              @click="showProtectedStatusModal()"
                          >On Hold
                          </b-badge>
                          <br/>
                          <b>Until: </b>
                          <span v-if="user.banned_until">
                            {{ user.banned_until | dotDateTime }}</span
                          >
                          <span v-else>Infinite</span>
                        </template>
                        <template v-if="user.status_updated_at">
                          <br/>
                          <b>Last update: </b
                          >{{ user.status_updated_at | dotDateTime }}&nbsp;
                          <template v-if="user.status_by_admin_id">
                            <div class="d-inline-flex">
                              by &nbsp;
                              <UserBadgeName
                                  :color="user.admin_hex_color"
                                  :name="user.admin_name"
                              />
                            </div>
                          </template>
                        </template>
                        <template v-if="user.banned_message">
                          <br/>
                          <b>Comment: </b>{{ user.banned_message }}
                        </template>
                      </div>

                      <div class="col-1 p-0 d-flex align-items-center">
                        <b-icon
                            icon="journals"
                            scale="1.5"
                            @click="$refs['attribute_logs_modal'].show()"
                        ></b-icon>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr v-if="quality.trueface_status !== undefined && quality.trueface_status !== null">
                  <td>Human verification</td>
                  <td>
                    <div class="d-flex align-items-center" style="gap: 8px;">
                      <b-badge :variant="getTruefaceStatusVariant(quality.trueface_status)">
                        {{ getTruefaceStatusText(quality.trueface_status) }}
                      </b-badge>
                      <b-badge
                        v-if="quality.trueface_match !== null"
                        :variant="quality.trueface_match === 2 ? 'danger' : 'success'"
                        class="cursor-pointer"
                        @click="redirectToTruefaceReview"
                      >
                        {{ quality.trueface_match === 2 ? 'Match' : 'No Match' }}
                      </b-badge>
                      <b-button
                        v-if="canViewPhoto"
                        size="sm"
                        variant="outline-primary"
                        @click="showTruefaceImageModal"
                      >
                        View Photo
                      </b-button>
                    </div>
                  </td>
                </tr>
                </tbody>
                <tfoot>
                <tr>
                  <td class="text-nowrap" style="padding-left: 0px">
                    Created
                    {{ user.created_at | dotDateTime }}
                  </td>
                  <td
                      class="text-nowrap"
                      style="padding-right: 0px; text-align: right"
                  >
                    <div v-if="user.last_click">
                      Last click
                      {{ user.last_click.created_at | dotDateTime }}
                    </div>
                    <div v-else>
                      <i>No click data yet</i>
                    </div>
                  </td>
                </tr>
                </tfoot>
              </table>

              <UserActionsBox
                  :can_trigger_verification.sync="can_trigger_verification"
                  :can_require_facetec="can_require_facetec"
                  :trueface_status="quality ? quality.trueface_status : null"
                  :is-display-inline="true"
                  :phone_verifications.sync="phone_verifications"
                  :user.sync="user"
                  @facetec-required="refreshUserDetails"
                  @unavailable-review="$emit('unavailable-review')"
                  :quality="quality"
                  class="mt-2"
              />

              <UserNotes
                  :user-id="user.id"
                  class="mt-3"
              />
            </div>
            <div style="min-width: 30%">
              <div>
                <h3>Performance</h3>
                <table
                    class="table b-table table-striped table-hover"
                    style="min-width: 200px"
                >
                  <tbody>
                  <tr>
                    <td>Clicks</td>
                    <td>{{ performance.total_clicks }}</td>
                  </tr>
                  <tr>
                    <td>Completes</td>
                    <td>{{ performance.total_completes }}</td>
                  </tr>
                  <tr>
                    <td>Conversion</td>
                    <td>{{ performance.conversion | round2Decimal }} %</td>
                  </tr>
                  <tr>
                    <td>Survey Revenue</td>
                    <td>{{ performance.survey_revenue | formatCurrency }}</td>
                  </tr>
                  <tr>
                    <td>Survey Publisher Payout</td>
                    <td>{{ user.survey_publisher_payout | formatCurrency }}</td>
                  </tr>
                  <tr>
                    <td>Offer Clicks</td>
                    <td>{{ user.offer_clicks_count }}</td>
                  </tr>
                  <tr>
                    <td>Offer Events</td>
                    <td>{{ user.offer_events_count }}</td>
                  </tr>
                  <tr>
                    <td>Offer Revenue</td>
                    <td>{{ user.offer_revenue | formatCurrency }}</td>
                  </tr>
                  <tr>
                    <td>Offer Publisher Payout</td>
                    <td>
                      <span
                          :id="`offer-publisher-payout-${user.id}`"
                          class="text-primary cursor-pointer"
                      >
                        {{ user.offer_publisher_payout | formatCurrency }}
                      </span>
                      <b-popover
                          :target="`offer-publisher-payout-${user.id}`"
                          triggers="hover focus"
                          placement="left"
                      >
                        <table class="table table-sm table-borderless mb-0">
                          <tbody>
                          <tr
                              v-for="status in offerPublisherPayoutSummary"
                              :key="status.key"
                          >
                            <td :class="['pr-2', 'text-nowrap', status.textClass]">
                              {{ status.label }}
                            </td>
                            <td :class="['text-right', 'text-nowrap', status.textClass]">
                              {{ status.value | formatCurrency }}
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </b-popover>
                    </td>
                  </tr>
                  <tr>
                    <td>In-App Purchases (IAPs)</td>
                    <td class="text-primary cursor-pointer" @click="showIapDataModal">{{ user.iap_spend | formatCurrency }}</td>
                  </tr>
                  <tr>
                    <td>User Profitability Score</td>
                    <td :class="getProfitabilityScoreClass()">
                      {{ getProfitabilityScoreDisplay() }}
                      <span v-if="getProfitabilityScoreStars() > 0">
                        <b-icon
                          v-for="star in getProfitabilityScoreStars()"
                          :key="star"
                          icon="star-fill"
                          class="ml-1"
                        />
                      </span>
                    </td>
                  </tr>
                  <tr v-if="user.user_payouts !== undefined && user.user_payouts !== null">
                    <td>User Payouts</td>
                    <td>{{ user.user_payouts | formatCurrency }} ({{ user.user_payouts_count }} Payouts)</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div class="mt-5">
                <h3>Connected Accounts</h3>
                <div style="overflow-x: auto; max-width: 100%;">
                  <table
                      class="table b-table table-striped table-hover"
                      style="min-width: 250px"
                  >
                    <thead>
                    <tr>
                      <th>User ID</th>
                      <th>App</th>
                      <th>Pub. Payout</th>
                      <th>IAP Data</th>
                      <th>Attribute</th>
                      <th>Match</th>
                      <th style="width: 60px" title="Pinned connections persist past dynamic-match thresholds">Pin</th>
                    </tr>
                    </thead>
                    <tbody>
                    <template v-if="limitedConnectedAccounts.length === 0">
                      <tr>
                        <td colspan="7" class="text-center">
                          Recent IP results load on demand. Use the action below.
                        </td>
                      </tr>
                    </template>
                    <template v-else>
                      <tr v-for="account in limitedConnectedAccounts" :key="account.user_id">
                        <td style="max-width: 100px">
                          <UserElement :id="account.user_id" :status="account.status"/>
                        </td>
                        <td>
                          <AppWithLogo :appName="account.app_name" />
                        </td>
                        <td style="white-space: nowrap">{{ account.publisher_payout_sum | newFormatCurrencyDecimals }}</td>
                        <td style="white-space: nowrap">{{ account.iap_data | newFormatCurrencyDecimals }}</td>
                        <td>{{ account.formatted_matching_attributes }}</td>
                        <td :class="getProbabilityClass(account.matching_probability)">
                          {{ account.matching_probability }}%
                        </td>
                        <td class="text-center">
                          <b-button
                            variant="link"
                            size="sm"
                            class="p-0"
                            :class="{'text-warning': account.is_pinned}"
                            :disabled="pinPendingIds.includes(account.user_id)"
                            v-b-tooltip.hover.top="account.is_pinned ? 'Unpin' : 'Pin'"
                            @click="togglePreviewPin(account)"
                          >
                            <feather-icon
                              icon="MapPinIcon"
                              :fill="account.is_pinned ? 'currentColor' : 'none'"
                              size="16"
                            />
                          </b-button>
                        </td>
                      </tr>
                    </template>
                    <tr>
                      <td>
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          @click="openRelatedAccountsModal()"
                        >
                          View
                        </b-button>
                      </td>
                      <td
                          colspan="6"
                          style="font-size: 12px"
                          :class="[
                        hasRelatedAccountsSummary && relatedAccounts.summary.flagged_rate && parseFloat(relatedAccounts.summary.flagged_rate) >= 0.2 ? 'bg-danger' : '',
                        'text-center',
                      ]"
                      >
                        <strong v-if="hasRelatedAccountsSummary">
                          {{ relatedAccounts.summary.flagged_users }}/{{ relatedAccounts.summary.total_users }} banned
                          ({{ relatedAccounts.summary.flagged_rate ? relatedAccounts.summary.flagged_rate : 0 | percentageRound }})
                        </strong>
                        <span v-else class="text-muted">Recent IP filters load on demand.</span>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
            <div style="min-width: 23%">
              <div class="d-flex flex-column align-items-start mb-1">
                <div class="mb-auto w-100">
                  <h3>Publisher Payout</h3>

                  <div class="p-chart-container">
                    <vue-apex-charts
                        type="bar"
                        height="250"
                        :series="publisherPayoutChart.series"
                        :options="publisherPayoutChartOptions"
                    ></vue-apex-charts>
                  </div>
                </div>
              </div>

              <div
                  class="mb-1"
                  style="display: flex; justify-content: space-between; gap: 20px"
              >
                <div style="min-width: 45%">
                  <h3>Settings</h3>
                  <table
                      class="table b-table table-striped table-hover"
                      style="min-width: 250px"
                  >
                    <tbody>
                    <tr>
                      <td>Research Defender</td>
                      <td>
                        <b-form-checkbox
                            v-model="user.research_defender"
                            switch
                            @change="changeStatus('research_defender')"
                        ></b-form-checkbox>
                      </td>
                    </tr>
                    <tr>
                      <td>VPN/Proxy Check</td>
                      <td>
                        <b-form-checkbox
                            v-model="user.vpn_proxy_check"
                            switch
                            @change="changeStatus('vpn_proxy_check')"
                        ></b-form-checkbox>
                      </td>
                    </tr>
                    <tr>
                      <td>IP Location Check</td>
                      <td>
                        <b-form-checkbox
                            v-model="user.ip_location_check"
                            switch
                            @change="changeStatus('ip_location_check')"
                        ></b-form-checkbox>
                      </td>
                    </tr>
                    <tr>
                      <td>Show offers</td>
                      <td>
                        <!-- View mode -->
                        <b-badge
                          v-if="!offerStatusEditing"
                          :variant="offerStatusVariant(user.offers_status)"
                          class="cursor-pointer"
                          :class="offerStatusExtraClass(user.offers_status)"
                          title="Click to change offer status"
                          @click.stop="offerStatusStartEditing"
                        >
                          {{ offerStatusLabel(user.offers_status) }}
                        </b-badge>

                        <!-- Edit mode -->
                        <div v-else class="d-flex align-items-center">
                          <b-form-select
                            v-model.number="user.offers_status"
                            :options="offerStatusOptions"
                            @change="offerStatusApply"
                            size="sm"
                            class="mr-1"
                          />
                          <b-button size="sm" variant="outline-secondary" @click="offerStatusCancel">
                            Cancel
                          </b-button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>Payout Adjustment</td>
                      <td>
                        <b-input-group append="%" style="width: 100px">
                          <b-form-input
                              v-model="user.payout_adjustment"
                              :state="payoutAdjustmentValidate"
                              max="100"
                              min="10"
                              type="number"
                              @change="updatePayoutAdjustment($event)"
                              @input="userPayoutChanged = true"
                          ></b-form-input>
                        </b-input-group>
                      </td>
                    </tr>
                    <tr>
                      <td>Min Pub. Payout</td>
                      <td>
                        <b-input-group prepend="$" style="width: 100px">
                          <b-form-input
                              v-model="user.min_publisher_payout"
                              :disabled="!isCurrentUserSupremeSuperAdminOrQA"
                              type="number"
                              @change="updateMinPublisherPayout($event)"
                          ></b-form-input>
                        </b-input-group>
                      </td>
                    </tr>
                    <tr>
                      <td>Max Recon. Buyers</td>
                      <td>
                        <b-input-group append="%" style="width: 100px">
                          <b-form-input
                              v-model="user.max_reconciliation_buyers"
                              :disabled="!isCurrentUserSupremeSuperAdminOrQA"
                              :state="maxReconciliationBuyersValidate"
                              max="150"
                              type="number"
                              @change="updateMaxReconciliationBuyers($event)"
                              @input="userMaxReconciliationBuyers = true"
                          ></b-form-input>
                        </b-input-group>
                      </td>
                    </tr>
                    <tr>
                      <td>Limit Completes</td>
                      <td>
                        <div class="d-flex align-items-center" style="gap: 5px">
                          <b-form-checkbox
                              v-model="completeLimitEnabled"
                              :disabled="completeLimitSaving"
                              switch
                              @change="toggleCompleteLimit"
                          ></b-form-checkbox>
                          <b-form-input
                              v-if="completeLimitEnabled"
                              v-model.number="completeLimitValue"
                              :disabled="completeLimitSaving"
                              :state="completeLimitValidate"
                              max="1000"
                              min="1"
                              size="sm"
                              style="width: 80px"
                              type="number"
                              @change="saveCompleteLimit"
                          ></b-form-input>
                        </div>
                        <div
                            v-if="user.complete_limit"
                            class="d-flex align-items-center flex-wrap mt-25"
                            style="gap: 4px"
                        >
                          <template v-if="user.complete_limit.triggered_at">
                            <b-badge variant="warning">Reached</b-badge>
                            <small class="text-muted">{{ user.complete_limit.triggered_at | dotDateTime }}</small>
                            <small class="text-muted">counted from {{ user.complete_limit.created_at | dotDateTime }}</small>
                          </template>
                          <template v-else>
                            <small class="text-muted">counting since {{ user.complete_limit.created_at | dotDateTime }}</small>
                          </template>
                          <UserBadgeName
                              v-if="user.complete_limit.admin_name"
                              :color="user.complete_limit.admin_hex_color"
                              :name="user.complete_limit.admin_name"
                          />
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                  <div class="text-center mt-1">
                    <b-button
                        :disabled="!user.birthday"
                        variant="outline-danger"
                        @click="showConfirmResetBirthdate"
                    >Reset birthdate
                    </b-button>
                  </div>
                </div>
              </div>
              <div>
              <textarea
                  v-model="user.comment"
                  class="form-control"
                  placeholder="User comment"
                  style="min-height: 60px; margin-bottom: 5px"
                  @blur="autoSaveComment"
              ></textarea>
                <div v-if="user.comment_updated_at" class="d-flex float-right">
                  Last updated at:
                  <b>{{ user.comment_updated_at | dotDateTime }}</b>
                  <template v-if="user.comment_updated_admin_id">
                    <div class="d-inline-flex ml-4">
                      <UserBadgeName
                          :color="user.comment_admin_hex_color"
                          :name="user.comment_admin_name"
                      />
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <div v-if="user.payout_adjustment_history" class="mt-2 w-100">
            <h5>Payout Adjustment History</h5>
            <div
                v-if="user.payout_adjustment_history.length"
                class="table-responsive"
            >
              <table class="table b-table table-striped table-hover my-1">
                <thead>
                  <tr>
                    <th>Original Payout</th>
                    <th>Adjusted Payout</th>
                    <th>Adjusted By</th>
                    <th>Date/Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                      v-for="(entry, index) in user.payout_adjustment_history"
                      :key="index"
                  >
                    <td>{{ entry.original_payout }}%</td>
                    <td>{{ entry.adjusted_payout }}%</td>
                    <td>
                      <span v-if="entry.adjusted_by">{{ entry.adjusted_by }}</span>
                      <span v-else>-</span>
                    </td>
                    <td class="text-nowrap">
                      <span v-if="entry.date_time">{{ entry.date_time | dotDateTime }}</span>
                      <span v-else>-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-muted">No history</div>
          </div>
        </b-card-header>

      </div>
      <div class="card">
        <b-card-header>
          <div
              style="
            display: flex;
            justify-content: flex-start;
            width: 100%;
            gap: 5%;
          "
          >
            <div style="min-width: 27%">
              <h3>Quality</h3>
              <div
                  v-if="quality.is_suspicious_activity_pattern"
                  class="bg-danger text-white p-50 my-50 mr-50"
                  style="display: inline-block"
              >
                Suspicious Activity Pattern
              </div>
              <div
                  v-if="shouldShowSameOffersTaken"
                  class="bg-warning text-white p-50 my-50 ml-50 cursor-pointer"
                  style="display: inline-block"
                  @click="showSameOffersTakenModal"
              >
                <u>Same Offers Taken</u>
              </div>
              <div
                  v-if="quality.is_suspicious_click_pattern"
                  class="bg-danger text-white p-50 my-50"
                  style="display: inline-block"
              >
                Suspicious Click Pattern
              </div>
              <div
                  v-if="quality.suspicious_user_profiling.flag"
                  class="bg-danger text-white p-50 my-50 ml-50 cursor-pointer"
                  style="display: inline-block"
                  @click="showSuspiciousUserProfilingModal"
              >
                <u>Suspicious User Profiling</u>
              </div>
              <div
                  v-if="quality && quality.high_risk_subnets && quality.high_risk_subnets.length"
                  class="bg-danger text-white p-50 my-50 cursor-pointer"
                  style="display: inline-block"
                  @click="showHighRiskSubnetsModal"
              >
                <u>{{ quality.high_risk_subnets.length }} High-risk IP Subnets</u>
              </div>
              <div
                  v-if="quality && quality.high_risk_ip_ranges && quality.high_risk_ip_ranges.length"
                  class="bg-danger text-white p-50 my-50 cursor-pointer ml-1"
                  style="display: inline-block"
                  @click="showHighRiskIpRangesModal"
              >
                <u>{{ quality.high_risk_ip_ranges.length }} High-risk IP Ranges</u>
              </div>
              <div
                  v-if="quality && quality.high_risk_cities && quality.high_risk_cities.length"
                  class="bg-danger text-white p-50 my-50 cursor-pointer ml-1"
                  style="display: inline-block"
                  @click="showHighRiskCitiesModal"
              >
                <u>{{ quality.high_risk_cities.length }} High-risk Cities</u>
              </div>
              <div
                  v-if="quality && quality.high_risk_signup_ips && quality.high_risk_signup_ips.length"
                  class="bg-danger text-white p-50 my-50 cursor-pointer ml-1"
                  style="display: inline-block"
                  @click="showHighRiskSignupIpsModal"
              >
                <u>{{ quality.high_risk_signup_ips.length }} High-risk Signup IPs</u>
              </div>
              <div
                  v-if="quality && quality.claim_access_country_mismatch && quality.claim_access_country_mismatch.length"
                  class="bg-danger text-white p-50 my-50 cursor-pointer ml-1"
                  style="display: inline-block"
                  @click="showClaimAccessCountryMismatchModal"
              >
                <u>Claim Access Country Mismatch</u>
              </div>
              <table class="table b-table table-striped table-hover">
                <tbody>
                <tr>
                  <td class="text-nowrap">
                    <router-link
                        :to="{
                        name: 'clicks',
                        query: { 'filter[user_id]': user.id, 'filter[suspicious_response_codes]': true },
                      }"
                      target="_blank"
                    >
                      Suspicious Response Codes
                    </router-link>
                  </td>
                  <template v-if="quality">
                    <td :class="classSuspiciousResponseCode">
                      {{ quality.suspicious_response_percentage.toFixed(1) }} %
                      ({{ quality.suspicious_response_count }})
                    </td>
                  </template>
                  <td v-else class="text-nowrap">No result</td>
                </tr>
                <tr>
                  <td class="text-nowrap">Avg Publisher CPI</td>
                  <template v-if="quality.click_additional_metrics">
                    <td :class="classAvgPublisherCPI" class="text-nowrap">
                      {{ quality.click_additional_metrics.avg_cpi | formatCurrency }}
                    </td>
                  </template>
                  <td v-else class="text-nowrap">No result</td>
                </tr>
                <tr>
                  <td class="text-nowrap">Earnings per Click</td>
                  <template v-if="quality.click_additional_metrics">
                    <td :class="classEarningPerClick" class="text-nowrap">
                      {{ quality.click_additional_metrics.earnings_per_click | formatCurrency }}
                    </td>
                  </template>
                  <td v-else class="text-nowrap">No result</td>
                </tr>
                <tr>
                  <td class="text-nowrap">Time in Completes</td>
                  <template v-if="quality.click_additional_metrics">
                    <td :class="classTimeInCompletes" class="text-nowrap">
                      {{ quality.click_additional_metrics.time_in_completes_percentage | percentageRound }}
                    </td>
                  </template>
                  <td v-else class="text-nowrap">No result</td>
                </tr>
                <tr>
                  <td class="text-nowrap">Overlapping Completes</td>
                  <template v-if="quality.overlap_rate">
                    <td :class="classOverlappingCompletes" class="text-nowrap">
                      {{ quality.overlap_rate | percentageRound }}
                    </td>
                  </template>
                  <td v-else class="text-nowrap">No result</td>
                </tr>
                <tr>
                  <td class="text-nowrap">Completes Factor</td>
                  <td :class="classCompletesFactor" class="text-nowrap">
                    {{ completesFactor.total }}
                    <template v-if="completesFactor.total_alert > 0">
                      ({{ completesFactor.total_alert }} alerts)
                    </template>
                  </td>
                </tr>
                <tr>
                  <td class="text-nowrap">Median TCP Latency</td>
                  <template
                      v-if="
                      quality && quality.the_second_median_tcp_latency != null
                    "
                  >
                    <td
                        :class="classTheSecondMedianTcpLatency"
                        class="text-nowrap"
                    >
                      {{
                        parseFloat(quality.the_second_median_tcp_latency)
                            | round2Decimal
                      }}
                      ms
                    </td>
                  </template>
                  <td v-else>No result</td>
                </tr>
                <tr>
                  <td class="text-nowrap">
                    <router-link
                        :to="{
                        name: 'latency_logs',
                        query: { 'filter[userId]': user.id },
                      }"
                      target="_blank"
                    >
                      Median WS Latency
                    </router-link>
                  </td>
                  <template
                      v-if="
                      quality && quality.the_second_median_ws_latency != null
                    "
                  >
                    <td
                        :class="classTheSecondMedianWSLatency"
                        class="text-nowrap"
                    >
                      {{
                        parseFloat(quality.the_second_median_ws_latency)
                            | round2Decimal
                      }}
                      ms
                    </td>
                  </template>
                  <td v-else>No result</td>
                </tr>
                <tr>
                  <td class="text-nowrap">
                    <router-link
                        :to="{
                        name: 'latency_logs',
                        query: { 'filter[userId]': user.id },
                      }"
                      target="_blank"
                    >
                      Median Latency Delta
                    </router-link>
                  </td>
                  <template
                      v-if="
                      quality && quality.the_second_median_latency_delta != null
                    "
                  >
                    <td
                        :class="classTheSecondMedianLatencyDelta"
                        class="text-nowrap"
                    >
                      {{
                        parseFloat(quality.the_second_median_latency_delta)
                            | round2Decimal
                      }}
                      ms
                    </td>
                  </template>
                  <td v-else class="bg-danger text-white">No value</td>
                </tr>
                <tr>
                  <td class="text-nowrap">
                    <router-link
                        :to="{
                        name: 'latency_logs',
                        query: { 'filter[userId]': user.id },
                      }"
                      target="_blank"
                    >
                      Latency Results
                    </router-link>
                  </td>
                  <template
                      v-if="quality && quality.the_second_latency_results != null"
                  >
                    <td
                        :class="classTheSecondLatencyResults"
                        class="text-nowrap"
                    >
                      {{ quality.the_second_latency_results | percentageRound }}
                    </td>
                  </template>
                  <td v-else>No result</td>
                </tr>
                <tr>
                  <td class="text-nowrap">
                    <router-link
                        :to="{
                        name: 'device_results',
                        query: { 'filter[userId]': user.id },
                      }"
                      target="_blank"
                    >
                      Device OS Mismatch
                    </router-link>
                  </td>
                  <template
                      v-if="
                      quality && quality.device_os_mismatch_percentage != null
                    "
                  >
                    <td :class="classDeviceOsMismatch" class="text-nowrap">
                      {{
                        quality.device_os_mismatch_percentage | percentageRound
                      }}
                    </td>
                  </template>
                  <td v-else>No result</td>
                </tr>
                <tr>
                  <td class="text-nowrap">
                    <router-link
                        :to="{
                        name: 'timezone_results',
                        query: { 'filter[userId]': user.id },
                      }"
                      target="_blank"
                    >
                      Max Time Delta
                    </router-link>
                  </td>
                  <template v-if="quality && quality.max_time_delta != null">
                    <td :class="classMaxTimeDelta" class="text-nowrap">
                      {{ quality.max_time_delta }} hours
                    </td>
                  </template>
                  <td v-else>No result</td>
                </tr>
                <tr>
                  <td class="text-nowrap">
                    <router-link
                        :to="{
                        name: 'timezone_results',
                        query: { 'filter[userId]': user.id },
                      }"
                      target="_blank"
                    >
                      Timezone Mismatch
                    </router-link>
                  </td>
                  <template v-if="quality && quality.timezone_mismatch.length">
                    <td :class="classTimezoneMismatch">
                      {{ quality.timezone_mismatch.join(", ") }}
                    </td>
                  </template>
                  <td v-else class="bg-success text-white">-</td>
                </tr>
                <tr>
                  <td class="text-nowrap">
                    <router-link
                        :to="{
                        name: 'ip_logs',
                        query: { 'filter[userId]': user.id },
                      }"
                      target="_blank"
                    >
                      Country Mismatch
                    </router-link>
                  </td>
                  <template v-if="quality && quality.country_mismatch.length">
                    <td :class="classCountryMismatch">
                      {{ quality.country_mismatch.join(", ") }}
                    </td>
                  </template>
                  <td v-else class="bg-success text-white">-</td>
                </tr>
                <tr>
                  <td class="text-nowrap">Claim Access from:</td>
                  <template v-if="quality && quality.claim_access_country_mismatch && quality.claim_access_country_mismatch.length">
                    <td class="bg-danger text-white cursor-pointer" @click="showClaimAccessCountryMismatchModal">
                      <u>{{ quality.claim_access_country_mismatch.length }}</u>
                    </td>
                  </template>
                  <td v-else class="bg-success text-white">-</td>
                </tr>
                <tr>
                  <td class="text-nowrap">
                    <a href="#" @click.prevent="openPatternRiskModal">
                      Pattern Risk
                    </a>
                  </td>
                  <td :class="classPatternRisk" class="text-nowrap">
                    {{ formattedPatternRiskText }}
                  </td>
                </tr>
                <tr>
                  <td class="text-nowrap">Supplier Bans</td>
                  <template
                      v-if="supplier_statistics && supplier_statistics.length"
                  >
                    <td :class="classSupplierBansQuality" class="text-nowrap">
                      {{
                        supplier_statistics.filter(
                            (supplier) => supplier.user_status === "banned"
                        ).length
                      }}
                    </td>
                  </template>
                  <td v-else>No result</td>
                </tr>
                <tr>
                  <td class="text-nowrap">Detected Languages</td>
                  <template v-if="quality && quality.detected_languages">
                    <td class="bg-danger text-white">
                      {{ quality.detected_languages }}
                    </td>
                  </template>
                  <td v-else class="bg-success text-white text-nowrap">
                    No result
                  </td>
                </tr>
                <tr>
                  <td class="text-nowrap">RDP Usage</td>
                  <template
                      v-if="
                      quality &&
                      quality.ipqs_rdp_data &&
                      quality.ipqs_rdp_data.rdp_usage
                    "
                  >
                    <td :class="classRDPResult">
                      {{ quality.ipqs_rdp_data.rdp_usage }} %
                    </td>
                  </template>
                  <td v-else class="bg-success text-white text-nowrap">
                    No result
                  </td>
                </tr>
                <tr>
                  <td class="text-nowrap">IPQS Score</td>
                  <template
                      v-if="
                      quality &&
                      quality.ipqs_rdp_data &&
                      quality.ipqs_rdp_data.ipqs_unique_ips_avg
                    "
                  >
                    <td :class="classIPQSScore">
                      {{ parseInt(quality.ipqs_rdp_data.ipqs_unique_ips_avg) }}
                    </td>
                  </template>
                  <td v-else class="bg-success text-white text-nowrap">
                    No result
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
            <div style="min-width: 25%">
              <div class="mb-3">
                <h3>Reconciliations</h3>
                <table
                    class="table b-table table-striped table-hover"
                    style="max-width: 160px"
                >
                  <thead>
                  <tr>
                    <th>Month</th>
                    <th>Stats</th>
                    <th>Current</th>
                    <th>Projected</th>
                    <th>Confidence</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr
                      v-for="(
                      reconciliation, iReconciliation
                    ) in reconciliation.data"
                      :key="iReconciliation"
                  >
                    <td style="width: 60px">{{ reconciliation.month }}</td>
                    <td class="text-nowrap" style="width: 45px">
                      <span class="text-danger">{{
                          reconciliation.reconciled_count
                        }}</span>
                      <span>/ {{ reconciliation.completes_count }}</span>
                      <span
                          v-if="reconciliation.high_recon_surveys_excluded > 0"
                      >
                        <b-icon
                            :id="`tooltip-high-recon-surveys-excluded-${iReconciliation}`"
                            class="border border-black mb-25 ml-50"
                            icon="star-fill"
                            scale=".85"
                        ></b-icon>
                        <b-tooltip
                            :target="`tooltip-high-recon-surveys-excluded-${iReconciliation}`"
                            triggers="hover"
                        >
                          <div class="font-small-4">
                            {{
                              reconciliation.high_recon_surveys_excluded
                            }}
                            surveys excluded for high recon
                          </div>
                        </b-tooltip>
                      </span>
                    </td>
                    <td
                        :class="
                        getClassReconciliationPercentage(
                          reconciliation.current * 100
                        )
                      "
                    >
                      <span>
                        {{ reconciliation.current | percentageRound }}</span
                      >
                    </td>
                    <td
                        v-if="reconciliation.is_show_projected"
                        :class="
                        getClassReconciliationPercentage(
                          reconciliation.projected * 100
                        )
                      "
                        class="text-nowrap"
                    >
                      <span v-if="reconciliation.projected"
                      >≈
                        {{ reconciliation.projected | percentageRound }}</span
                      >
                    </td>
                    <td v-else></td>
                    <td
                        v-if="reconciliation.is_show_projected"
                        :class="
                        getConfidence(
                          reconciliation.avg_days_since_complete
                        ).cssClass
                      "
                        class="text-nowrap"
                    >
                      <span v-if="reconciliation.avg_days_since_complete"
                      >
                        {{ getConfidence(reconciliation.avg_days_since_complete).text }}</span
                      >
                    </td>
                    <td v-else></td>
                  </tr>
                  <tr v-if="reconciliation.total > 0">
                    <td class="text-nowrap" colspan="2">
                      <router-link
                          :to="{
                          name: 'completes',
                          query: {
                            'filter[userId]': user.id,
                            'filter[dateRangeFrom]':
                              reconciliation.data[0].first_day_of_month,
                            'filter[status]': 2,
                          },
                        }"
                      >
                        Check Reconciliations
                      </router-link>
                    </td>
                    <td
                        :class="
                        getClassReconciliationPercentage(reconciliation.total)
                      "
                        class="text-nowrap text-center"
                        colspan="2"
                    >
                      {{ reconciliation.total }} %
                    </td>
                  </tr>
                  <tr>
                    <td v-if="isLoadingLifetime" colspan="5">
                      <b-spinner
                          small
                          v-if="isLoadingLifetime"
                          label="Loading..."
                          class="mr-1"
                      />
                    </td>
                    <td v-if="!isLoadingLifetime" colspan="2" class="text-left">
                      <div
                          class="text-nowrap text-primary cursor-pointer"
                          @click="$refs.lifetimeModal.show()"
                      >
                        Lifetime Reconciliation
                      </div>
                    </td>
                    <td v-if="!isLoadingLifetime" colspan="2" class="text-center">
                      {{ totalLifetimePercentage }}%
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <h3 class="mb-1">Quality Questions</h3>

              <div v-for="(qualityQuestion, iQualityQuestion)  in quality_questions" :key="iQualityQuestion.id" class="mb-2">
                <div v-if="qualityQuestion.open_end">
                  <h5>{{ qualityQuestion.open_end.question_text }}</h5>

                  <div v-if="qualityQuestion.open_end.answer_text.includes('/cp')" class="text-right mb-50"><span class="p-50 bg-danger font-weight-bold">Copy/Pasted</span></div>
                  <div
                      style="
                      padding: 5px 10px;
                      border: 1px solid #c3c3c3;
                      min-height: 60px;
                    "
                  >
                    {{ qualityQuestion.open_end.answer_text.replace('/cp', '') }}
                  </div>

                  <div class="row">
                    <div class="col-4 d-flex align-items-center">
                      <div
                          v-if="qualityQuestion.open_end.duplicate_count || qualityQuestion.open_end.similar_count"
                          class="text-primary text-nowrap cursor-pointer d-flex align-items-center mb-2"
                          @click="showSimilarUserProfiling(qualityQuestion.open_end.user_profiling_id)"
                      >
                        <span class="mr-1"><b>{{ qualityQuestion.open_end.duplicate_count }}</b> Identical</span>
                        <span><b>{{ qualityQuestion.open_end.similar_count }}</b> Similar</span>
                      </div>
                      <span v-else class="text-nowrap d-flex align-items-center mb-2">No similar and duplicate</span>
                    </div>
                    <div class="col-8 d-flex align-items-center">
                      <div class="col-6 mb-2 font-weight-bold d-flex align-items-center">Banned Open End Duplicates</div>
                      <div v-if="qualityQuestion.open_end.similar_count > 0 || qualityQuestion.open_end.duplicate_count > 0"
                           :class="getClassBannedUserDuplicates(qualityQuestion.open_end)"
                           class="col-6 p-50 mb-2 d-flex align-items-center"
                      >
                        {{ qualityQuestion.open_end.banned_users_count_percentage | percentage }}
                        ({{ qualityQuestion.open_end.banned_users_duplicate_count }})
                      </div>
                      <div v-else class="p-50 mb-2 d-flex align-items-center">-</div>
                    </div>
                  </div>
                </div>

                <div v-else>
                  <h5>{{ qualityQuestion.question_text }}?</h5>

                  <div>
                    <span v-for="(answer, iAnswer) in qualityQuestion.answers" :key="iAnswer" :class="{'text-primary': answer.is_answered}"><span v-if="iAnswer > 0">, </span>{{ answer.answer_text }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3>SMS Verification</h3>
              <table
                  v-for="phone_verification in phone_verifications"
                  :key="phone_verification.phone"
                  class="table b-table table-striped table-hover mb-1"
              >
                <tbody>
                <tr :key="phone_verification.phone" class="mb-1">
                  <td
                      class="text-center text-nowrap"
                      rowspan="4"
                      style="width: 130px"
                  >
                    <div class="block mb-1">
                      {{ phone_verification.phone || "---" }}
                    </div>
                    <b-badge
                        v-if="phone_verification.verified"
                        style="font-size: 11px"
                        variant="success"
                    >
                      Verified
                    </b-badge>
                    <b-badge
                        v-if="!phone_verification.verified"
                        style="font-size: 11px"
                        variant="secondary"
                    >
                      Not verified
                    </b-badge>
                    <div class="mt-2">{{ phone_verification.carrier }}</div>
                  </td>
                  <td class="text-nowrap">Verification Delay</td>
                  <td
                      :class="getClassVerificationDelay(phone_verification.delay)"
                      class="text-nowrap"
                  >
                    <span v-if="phone_verification.delay">
                      {{ phone_verification.delay | toHourMinuteSecond }}
                    </span>
                    <span v-else>---</span>
                  </td>
                </tr>

                <tr>
                  <td class="text-nowrap">Copy/Pasted</td>
                  <td
                      v-if="phone_verification.phone"
                      :class="
                      phone_verification.is_copy_pasted
                        ? 'bg-danger'
                        : 'bg-success'
                    "
                      class="text-nowrap"
                  >
                    <span class="text-white">{{
                        phone_verification.is_copy_pasted ? "True" : "False"
                      }}</span>
                  </td>
                  <td v-else></td>
                </tr>

                <tr>
                  <td class="text-nowrap">IPQS</td>
                  <td
                      v-if="phone_verification.phone"
                      :class="getClassIpqsScore(phone_verification.ipqs_score)"
                      class="text-nowrap"
                  >
                    {{ phone_verification.ipqs_score }}
                  </td>
                  <td v-else></td>
                </tr>

                <tr>
                  <td
                      class="text-nowrap"
                      :class="{'cursor-pointer text-primary': phone_verification.phone}"
                      @click="phone_verification.phone ? showUsersByCarrier(phone_verification.carrier) : false"
                  >
                    Carrier Risk
                  </td>
                  <td
                      v-if="phone_verification.phone"
                      :class="
                      getClassCarrierRisk(phone_verification.banned_percentage)
                    "
                      class="text-nowrap"
                  >
                    {{ phone_verification.banned_percentage | percentageRound }}
                  </td>
                  <td v-else></td>
                </tr>
                </tbody>
              </table>
              <div class="bg-secondary text-white p-1" v-if="!phone_verifications.length && !user.app_supports_verification">
                Verification not supported for this app
              </div>
            </div>
          </div>
        </b-card-header>
      </div>

    <div class="card">
      <b-card-header id="supplier-statistics" class="align-items-center">
        <div class="d-flex align-items-center">
          <h3 class="mb-0 mr-2">Supplier Statistics</h3>
          <b-form-checkbox v-model="showSuppliersWithoutClicks" switch class="mb-0">
            Show suppliers without clicks
          </b-form-checkbox>
        </div>
      </b-card-header>
      <div>
        <table class="table b-table table-striped table-hover">
          <thead role="rowgroup">
          <tr role="row">
            <th aria-colindex="1" aria-sort="descending" role="columnheader">
              Supplier
            </th>
            <th aria-colindex="2" aria-sort="none" role="columnheader">
              Status
            </th>
            <th aria-colindex="2" aria-sort="none" role="columnheader">
              Banned until
            </th>
            <th
                aria-colindex="3"
                aria-sort="none"
                role="columnheader"
                style="width: 180px"
            >
              Bans
            </th>
            <th
                aria-colindex="3"
                aria-sort="none"
                role="columnheader"
                style="width: 180px"
            >
              Resets
            </th>
            <th aria-colindex="4" aria-sort="none" role="columnheader">
              Clicks
            </th>
            <th aria-colindex="4" aria-sort="none" role="columnheader">
              Completes
            </th>
            <th aria-colindex="4" aria-sort="none" role="columnheader">
              Conversion
            </th>
            <th aria-colindex="4" aria-sort="none" role="columnheader">
              Factor
            </th>
            <th
                aria-colindex="4"
                aria-sort="none"
                class="text-nowrap"
                role="columnheader"
            >
              Buyer Bans
            </th>
            <th aria-colindex="4" aria-sort="none" role="columnheader">
              Actions
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, key) in filteredSupplierStatistics" :key="key">
            <td>
              <SupplierElement :id="item.id" :name="item.name"/>
            </td>
            <td>
              <b-badge
                  v-if="item.is_disabled_by_user || item.user_status === 'disabled'"
                  style="font-size: 11px"
                  variant="secondary"
                  @click="activateSupplierAgain(item)"
                  class="cursor-pointer"
              >Disabled
              </b-badge>
              <b-badge
                  v-else-if="item.user_status === 'active'"
                  style="font-size: 11px"
                  variant="success"
              >Active
              </b-badge>
              <b-badge
                  v-else-if="item.user_status === 'banned'"
                  style="font-size: 11px"
                  variant="danger"
                  @click="activateSupplierAgain(item)"
                  class="cursor-pointer"
              >Banned
              </b-badge>
            </td>
            <td>
              <template v-if="item.user_status !== 'active'">
                <template v-if="item.banned_until === null"
                >Infinite
                </template>
                <template v-else>{{
                    item.banned_until | dotDateTime
                  }}
                </template>
              </template>
              </td>
              <td>
                <span
                    v-if="item.user_bans.length > 0"
                    class="text-primary"
                    @click="showListUserBanModal(item)"
                >{{ item.user_bans.length }} ban(s)</span
                >
              </td>
              <td>
                <span
                    v-if="item.reset_hashes.length > 0"
                    class="text-danger"
                    @click="showResetHashesModal(item)"
                >{{ item.reset_hashes.length }} reset(s)</span
                >
              </td>
              <td>{{ item.total_clicks }}</td>
              <td>
                <span class="text-danger">{{
                    item.total_reconciliations
                  }}</span>
                /
                {{ item.total_completes }}
              </td>
              <td>{{ item.conversion | round2Decimal }} %</td>
              <td :class="getSupplierFactorClass(item)">
                {{ item.factor }}
              </td>
              <td class="text-nowrap">
                <span
                    v-if="item.banned_buyers_count > 0"
                    class="text-primary"
                    @click="showUserBuyerBans(item)"
                >{{ item.banned_buyers_count }} ban(s)</span
                >
              </td>
              <td class="d-flex text-nowrap" style="gap: 3px">
                <button
                    class="btn btn-danger btn-sm"
                    type="button"
                    @click="getSupplierBanFormVue().showForm(item.id, 5)"
                >
                  Ban
                </button>
                <button
                    class="btn btn-secondary btn-sm"
                    type="button"
                    @click="getSupplierBanFormVue().showForm(item.id, 6)"
                >
                  Disable
                </button>
                <button
                    class="btn btn-warning btn-sm"
                    type="button"
                    @click="resetUserDataOnSupplier(item.id)"
                >
                  Reset
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <b-card>
        <div class="p-chart-container">
          <div class="p-chart-header">
            Click Completes and Conversion Rates in 30 days
          </div>
          <vue-apex-charts
              :options="completesAndConversionsChartOptions"
              :series="completes_and_conversion_rates.series"
              height="450"
              type="line"
          ></vue-apex-charts>
        </div>
      </b-card>
      <ListUserBanModal :id="userId" ref="list_user_ban_modal"/>
      <ResetHashesModal :id="userId" ref="reset_hashes_modal"/>
      <b-modal ref="user_buyer_bans_modal" size="lg">
        <UserBuyerBans :userBuyerBans="currentBuyerBans"/>
      </b-modal>
      <b-modal ref="similar_user_profiling_modal" size="lg">
        <SimilarUserProfiling :similarUserProfilings="similarUserProfilings"/>
      </b-modal>
      <b-modal ref="user_domain_fraud" size="lg">
        <FraudEmailDomain :emailDomainFrauds="emailDomainFrauds"/>
      </b-modal>
      <b-modal ref="user_related_account_data" size="xl" title="Connected Accounts" hide-footer>
        <div class="d-flex align-items-center justify-content-between flex-wrap mb-1" style="gap: 0.75rem;">
          <div class="d-flex flex-wrap" style="gap: 0.5rem;">
            <b-button
              v-for="days in relatedAccountDayOptions"
              :key="`related-accounts-${days}`"
              size="sm"
              :variant="selectedRelatedAccountsDays === days ? 'primary' : 'outline-primary'"
              @click="changeRelatedAccountsDays(days)"
            >
              View all ({{ days }} days)
            </b-button>
          </div>
          <small class="text-muted">
            Recent IP: last {{ selectedRelatedAccountsDays }} days
          </small>
        </div>
        <div v-if="isRelatedAccountsModalLoading" class="py-3 text-center">
          <b-spinner variant="primary" label="Loading related accounts..." />
        </div>
        <div v-else-if="relatedAccountsModalError" class="py-2 text-center text-danger">
          {{ relatedAccountsModalError }}
        </div>
        <RelatedAccountData
          v-else-if="modalRelatedAccountsResults.length"
          :relatedAccountData="modalRelatedAccountsResults"
          :userId="userId"
          @pinned="onConnectedAccountPinned"
          @unpinned="onConnectedAccountUnpinned"
        />
        <p v-else class="mb-0 py-2 text-center">
          No related accounts found
        </p>
      </b-modal>
      <b-modal
          ref="user_profiling_basics_modal"
          size="lg"
          title="User Profiling Basics"
      >
        <UserProfilingBasics :userProfilingBasics="userProfilingBasics"/>
      </b-modal>
      <SupplierBanModal
          ref="supplier_ban_form"
          :userId="userId"
          @added="addedSupplierUserBanned"
      />
      <ProtectedUserStatusModal
          ref="protected_user_status"
          :isNeedApproval="false"
          :userId="userId"
          :status="user.status"
          :comment="user.banned_message"
          :completesCount="user.completes_count"
          :payoutOffersTotal="user.publisher_payout_offers_total"
          @activated="activatedUser"
          :user.sync="user"
      />
      <b-modal
          ref="attribute_logs_modal"
          :hide-footer="true"
          :hide-header="true"
          size="xl"
          scrollable
          body-class="pt-0"
      >
        <UserStatusLogModalContentNew :userId="user.id" />
      </b-modal>
      <b-modal ref="usersByCarrier" hide-footer size="md">
        <UsersByCarrier :carrier="currentCarrier"/>
      </b-modal>
      <b-modal
          ref="highRiskUserProfilingDetails"
          :hide-footer="true"
          :hide-header="true"
          centered
          size="xl"
      >
        <HighRiskUserProfilingTable
            :userProfiling="parseArray(quality.suspicious_user_profiling.data)"
        />
      </b-modal>
      <b-modal ref="iapDataModal" hide-header hide-footer size="lg">
        <div class="mb-1">IAP Data is up to 24 hours delayed</div>
        <b-table
            :items="iapData"
            :fields="[
              { key: 'name', label: 'Offer', sortable: true },
              { key: 'provider_id', label: 'Provider', sortable: true },
              { key: 'transaction_value_sum', label: 'Spend', sortable: true },
              { key: 'last_transaction_time', label: 'Last Spend', sortable: false },
          ]"
            striped hover responsive
        >
          <template #cell(name)="data">
            <OfferElement
                :id="data.item.offer_id"
                :name="data.value"
                :status="data.item.status"
                :visibility="data.item.visibility"
                :icon_url="data.item.icon_url"
            />
          </template>
          <template #cell(provider_id)="data">
            <OfferProviderElement :id="data.value" :name="data.item.provider_name" />
          </template>
          <template #cell(transaction_value_sum)="data">
            <div class="text-nowrap">{{ data.value | formatCurrency }}</div>
          </template>
        </b-table>
      </b-modal>

      <b-modal ref="sameOffersTakenModal" size="xl" title="Same Offers Taken">
        <SameOffersTaken :same-offers-taken="sameOffersTaken" />
      </b-modal>

      <!-- MODAL: Breaches list -->
      <b-modal ref="breachesModal" title="Breaches" hide-footer>
        <p v-if="emailBreaches && emailBreaches.breach_list">
          {{ emailBreaches.breach_list }}
        </p>
        <p v-else>No breach details available</p>
      </b-modal>

      <!-- MODAL: Connected Accounts list -->
      <b-modal ref="accountsConnectedModal" title="Connected Accounts" hide-footer>
        <ul v-if="connectedAccounts && connectedAccounts.accounts && connectedAccounts.accounts.length">
          <li v-for="(acc, idx) in connectedAccounts.accounts" :key="idx">
            {{ acc }}
          </li>
        </ul>
        <p v-else>No connected accounts found</p>
      </b-modal>

      <!-- MODAL: Trueface Image -->
      <b-modal ref="truefaceImageModal" title="Photo" hide-footer size="md" centered>
        <div class="text-center">
          <b-spinner v-if="truefaceImageLoading" variant="primary" label="Loading..."></b-spinner>
          <div v-else-if="truefaceImageError" class="text-muted">
            <p>{{ truefaceImageError }}</p>
          </div>
          <div v-else-if="!truefaceImageData" class="text-muted">
            <p>No image to show</p>
          </div>
          <img v-else :src="truefaceImageData" alt="Photo" style="max-width: 100%; height: auto;" />
        </div>
      </b-modal>

      <b-modal
          ref="patternRiskModal"
          title="Pattern Risk Users"
          size="xs"
      >
        <div class="mb-2">
          <strong>App ID:</strong> {{ patternRiskParams.app_id }}
          &nbsp; <strong>Country:</strong> {{ patternRiskParams.country_code }}
          &nbsp; <strong>External Source:</strong> {{ patternRiskParams.external_source }}
          &nbsp; <strong>Device Type:</strong> {{ quality.pattern_risk_device_type }}
        </div>
        <b-table
            :items="patternRiskList"
            :fields="[{ key: 'id', label: 'User ID' }]"
            empty-text="No Data Found"
            show-empty
            small
        >
          <template #cell(id)="data">
            <UserElement
                :id="data.value"
                :status="data.item.status"
            />
          </template>
        </b-table>
      </b-modal>


      <b-modal ref="lifetimeModal" title="Lifetime Reconciliation Summary" size="md">
        <b-table
            :items="lifetimeReconciliation"
            :fields="[
            { key: 'month', label: 'Month' },
            { key: 'reconciled_count', label: 'Reconciled' },
            { key: 'completes_count', label: 'Completes' },
            { key: 'percentage', label: 'Reconciliation %' }
          ]"
            striped
            responsive
        >
          <template #cell(percentage)="{ item }">
            <span v-if="item.completes_count > 0">
              <span v-if="item.reconciled_count === 0">0%</span>
              <span v-else>{{ ((item.reconciled_count / item.completes_count) * 100).toFixed(2) }}%</span>
            </span>
            <span v-else>-</span>
          </template>
        </b-table>

      </b-modal>

      <b-modal ref="highRiskSubnetsModal" title="High Risk Subnets" size="lg" hide-footer>
        <div style="max-height: 70vh; overflow-y: auto;">
          <b-table
            :items="quality && quality.high_risk_subnets ? quality.high_risk_subnets : []"
            :fields="highRiskSubnetsFields"
            striped hover responsive
          >
            <template #cell(ban_rate)="data">
              <span
                :class="{
                  'text-danger': data.value >= 50,
                  'text-warning': data.value >= 20 && data.value < 50,
                  'text-success': data.value < 20
                }"
              >
                {{ data.value }}%
              </span>
            </template>
            <template #cell(banned_users)="data">
              <span
                class="cursor-pointer text-primary"
                @click="showBannedUsersModal(data.item.users, data.item.subnet)"
              >
                <u>{{ data.value }}</u>
              </span>
            </template>
          </b-table>
        </div>
      </b-modal>

      <b-modal ref="highRiskIpRangesModal" title="High Risk IP Ranges" size="lg" hide-footer>
        <div style="max-height: 70vh; overflow-y: auto;">
          <b-table
            :items="quality && quality.high_risk_ip_ranges ? quality.high_risk_ip_ranges : []"
            :fields="highRiskIpRangesFields"
            striped hover responsive
          >
            <template #cell(ban_rate)="data">
              <span
                :class="{
                  'text-danger': data.value >= 50,
                  'text-warning': data.value >= 20 && data.value < 50,
                  'text-success': data.value < 20
                }"
              >
                {{ data.value }}%
              </span>
            </template>
            <template #cell(banned_users)="data">
              <span
                class="cursor-pointer text-primary"
                @click="showBannedUsersModalForRange(data.item.users, data.item.ip_range)"
              >
                <u>{{ data.value }}</u>
              </span>
            </template>
          </b-table>
        </div>
      </b-modal>

      <b-modal ref="highRiskCitiesModal" title="High Risk Cities" size="lg" hide-footer>
        <div style="max-height: 70vh; overflow-y: auto;">
          <b-table
            :items="quality && quality.high_risk_cities ? quality.high_risk_cities : []"
            :fields="highRiskCitiesFields"
            striped hover responsive
          >
            <template #cell(ban_rate)="data">
              <span
                :class="{
                  'text-danger': data.value >= 50,
                  'text-warning': data.value >= 20 && data.value < 50,
                  'text-success': data.value < 20
                }"
              >
                {{ data.value }}%
              </span>
            </template>
            <template #cell(banned_users)="data">
              <span
                class="cursor-pointer text-primary"
                @click="showBannedUsersModalForCity(data.item.users, data.item.city)"
              >
                <u>{{ data.value }}</u>
              </span>
            </template>
          </b-table>
        </div>
      </b-modal>

      <b-modal ref="highRiskSignupIpsModal" title="High Risk Signup IPs" size="lg" hide-footer>
        <div style="max-height: 70vh; overflow-y: auto;">
          <b-table
            :items="quality && quality.high_risk_signup_ips ? quality.high_risk_signup_ips : []"
            :fields="highRiskSignupIpsFields"
            striped hover responsive
          >
            <template #cell(ban_rate)="data">
              <span
                :class="{
                  'text-danger': data.value >= 50,
                  'text-warning': data.value >= 20 && data.value < 50,
                  'text-success': data.value < 20
                }"
              >
                {{ data.value }}%
              </span>
            </template>
            <template #cell(banned_users)="data">
              <span
                class="cursor-pointer text-primary"
                @click="showBannedUsersModalForSignupIp(data.item.users, data.item.signup_ip)"
              >
                <u>{{ data.value }}</u>
              </span>
            </template>
          </b-table>
        </div>
      </b-modal>

      <b-modal ref="claimAccessCountryMismatchModal" title="Claim Access Country Mismatch" size="md" hide-footer>
        <div style="max-height: 70vh; overflow-y: auto;">
          <ul class="list-group">
            <li
              v-for="country in (quality && quality.claim_access_country_mismatch ? quality.claim_access_country_mismatch : [])"
              :key="country.code || country"
              class="list-group-item d-flex align-items-center"
            >
              <country-flag v-if="getClaimAccessCountryCode(country)" :code-iso="getClaimAccessCountryCode(country).toLowerCase()" class="mr-1" />
              <div>
                {{ getClaimAccessCountryName(country) }}
                <span v-if="country.first_seen"> - first seen: {{ country.first_seen | dotDateTimeShort }}</span>
              </div>
            </li>
          </ul>
        </div>
      </b-modal>

      <b-modal ref="bannedUsersModal" :title="`Users in ${currentSubnet || currentIpRange || currentCity || currentSignupIp}`" size="md" hide-footer>
        <div style="max-height: 60vh; overflow-y: auto;">
          <b-table
            :items="selectedBannedUsers"
            :fields="bannedUsersFields"
            striped hover responsive
          >
            <template #cell(user_id)="data">
              <UserElement :id="data.value" :status="data.item.status" />
            </template>
            <template #cell(status)="data">
              <UserStatusIdBadge :status_id="data.value" />
            </template>
          </b-table>
        </div>
      </b-modal>
      <b-modal
        v-model="showEmailModal"
        :title="emailActionLabel"
        ok-title="Save"
        cancel-variant="outline-secondary"
        :ok-disabled="!isEmailValid"
        @ok.prevent="submitEmail"
      >
        <b-form-group label="Email" :state="isEmailValid" invalid-feedback="Enter a valid email address">
          <b-form-input
            v-model="emailModalValue"
            type="email"
            :state="isEmailValid"
            placeholder="name@example.com"
            @keyup.enter="isEmailValid && submitEmail()"
          />
        </b-form-group>
      </b-modal>
    </div>
  </div>
</template>

<script>
import ListUserBanModal from './UserDetails/ListUserBanModal.vue'
import ResetHashesModal from './UserDetails/ResetHashesModal.vue'
import UserBuyerBans from './UserDetails/UserBuyerBans.vue'
import SupplierBanModal from './UserDetails/SupplierBanModal.vue'
import ProtectedUserStatusModal from './UserDetails/ProtectedUserStatusModal.vue'
import VueApexCharts from 'vue-apexcharts'
import UserBadgeName from '@/views/users/UserBadgeName.vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import SimilarUserProfiling from '@/views/users/UserDetails/SimilarUserProfiling.vue'
import UserProfilingBasics from '@/views/users/UserDetails/UserProfilingBasics.vue'
import UsersByCarrier from '@/views/users/UserDetails/UsersByCarrier.vue'
import {$themeConfig} from '@themeConfig'
import POUserElement from '@/views/prime_opinion/elements/POUserElement.vue'
import FSUserElement from '@/views/five_surveys/elements/FSUserElement.vue'
import HCUserElement from '@/views/hey_cash/elements/HCUserElement.vue'
import TSUserElement from '@/views/top_surveys/elements/TSUserElement.vue'
import ESUserElement from '@/views/earn_star/elements/ESUserElement.vue'
import PTUserElement from '@/views/paid_tester/elements/PTUserElement.vue'
import UserActionsBox from '@/views/users/UserDetails/UserActionsBox.vue'
import UserNotes from '@/views/users/UserDetails/UserNotes.vue'
import QuickLinks from '@/main/custom-components/QuickLinks.vue'
import store from '@/store'
import PlatformDeviceIcon from '@/main/custom-components/PlatformDeviceIcon.vue'
import FraudEmailDomain from '@/views/users/UserDetails/FraudEmailDomain.vue'
import RelatedAccountData from '@/views/users/UserDetails/RelatedAccountData.vue'
import HighRiskUserProfilingTable from '@/views/resources/IpLogs/HighRiskUserProfilingTable.vue'
import AppElement from '@/views/elements/AppElement.vue'
import OfferElement from '@/views/resources/Offers/Parts/OfferElement.vue'
import OfferProviderElement from '@/views/elements/OfferProviderElement.vue'
import probabilityMixin from '@/views/users/mixins/probabilityMixin'
import AppWithLogo from '@/views/users/UserDetails/AppWithLogo.vue'
import SameOffersTaken from '@/views/users/UserDetails/SameOffersTaken.vue'
import UserStatusLogModalContentNew from '@/views/users/UserDetails/UserStatusLogModalContentNew.vue'
import UserStatusIdBadge from '@/views/users/UserDetails/UserStatusIdBadge.vue'

const RELATED_ACCOUNT_DAY_OPTIONS = [7, 14, 30]
const DEFAULT_RELATED_ACCOUNT_DAYS = 7

export default {
  name: 'UserDetails',
  mixins: [probabilityMixin],
  props: {
    id: [Number, String],
    status: Number,
    shownAsModal: Boolean,
    hideFullPageSpinner: Boolean,
    preloadOffersOverview: {
      type: Boolean,
      default: false
    }
  },
  components: {
    SameOffersTaken,
    AppWithLogo,
    ESUserElement,
    OfferProviderElement,
    OfferElement,
    TSUserElement,
    AppElement,
    HighRiskUserProfilingTable,
    HCUserElement,
    RelatedAccountData,
    FraudEmailDomain,
    PlatformDeviceIcon,
    QuickLinks,
    UserActionsBox,
    UserNotes,
    FSUserElement,
    POUserElement,
    UsersByCarrier,
    UserProfilingBasics,
    SimilarUserProfiling,
    UserBadgeName,
    VueApexCharts,
    ListUserBanModal,
    UserBuyerBans,
    SupplierBanModal,
    ProtectedUserStatusModal,
    ResetHashesModal,
    vSelect,
    UserStatusLogModalContentNew,
    UserStatusIdBadge,
    PTUserElement
  },
  directives: {
    Ripple
  },
  emits: ['update-user-settings', 'update-user-status', 'unavailable-review'],
  data () {
    return {
      primeOpinionLogo: null,
      fiveSurveysLogo: null,
      heyCashLogo: null,
      topSurveysLogo: null,
      earnStarLogo: null,
      paidTesterLogo: null,
      user: null,
      completeLimitEnabled: false,
      completeLimitValue: 5,
      completeLimitSaving: false,
      performance: null,
      quality: null,
      quality_questions: [],
      supplier_statistics: null,
      showSuppliersWithoutClicks: false,
      completes_and_conversion_rates: null,
      previousBanSupplier: null,
      phone_verifications: null,
      can_trigger_verification: false,
      can_require_facetec: false,
      reconciliation: {},
      countries: [],
      showEditLanguage: false,
      showEditInterfaceLanguage: false,
      isNeedApprovalToActivate: false,
      isNeedApprovalToBan: false,
      currentBuyerBans: [],
      similarUserProfilings: [],
      emailDomainFrauds: [],
      userProfilingBasics: [],
      userPayoutChanged: false,
      userMaxReconciliationBuyers: false,
      currentCarrier: null,
      sourceExtensionLogCount: 0,
      otherAccounts: [],
      relatedAccounts: {},
      relatedAccountData: [],
      relatedAccountDayOptions: RELATED_ACCOUNT_DAY_OPTIONS,
      selectedRelatedAccountsDays: DEFAULT_RELATED_ACCOUNT_DAYS,
      relatedAccountsModalData: null,
      relatedAccountsModalError: null,
      isRelatedAccountsModalLoading: false,
      relatedAccountsModalRequestId: 0,
      pinPendingIds: [],
      languageCodes: [],
      userInterfaceLanguage: null,
      showMinimalizeExternalId: true,
      isHighRiskEmailDomain: false,
      platformLastHundredClicks: null,
      staffReviewer: null,
      completesFactor: null,
      iapData: null,
      sameOffersTaken: null,
      publisherPayoutChart: {
        series: [],
        label: []
      },
      emailBreaches: null,
      connectedAccounts: null,
      patternRiskList: [],
      patternRiskParams: {
        app_id: null,
        country_code: null,
        external_source: null
      },
      lifetimeReconciliation: [],
      isLoadingLifetime: false,
      totalLifetimePercentage: null,
      offerStatusEditing: false,
      offerStatusPrev: null,
      offerStatusOptions: [
        { value: 1, text: 'Active' },
        { value: 2, text: 'Inactive' },
        { value: 3, text: 'Shadow-banned' }
      ],
      offerStatusMeta: {
        1: { label: 'Active', variant: 'success' },
        2: { label: 'Inactive', variant: 'danger' },
        3: { label: 'Shadow-banned', variant: 'user-status-3' }
      },
      highRiskSubnetsFields: [
        { key: 'subnet', label: 'Subnet (CIDR)', sortable: true },
        { key: 'total_users', label: 'Total Users', sortable: true },
        { key: 'banned_users', label: 'Banned Users', sortable: true },
        { key: 'ban_rate', label: 'Ban Rate (%)', sortable: true }
      ],
      highRiskIpRangesFields: [
        { key: 'ip_range', label: 'IP Range', sortable: true },
        { key: 'total_users', label: 'Total Users', sortable: true },
        { key: 'banned_users', label: 'Banned Users', sortable: true },
        { key: 'ban_rate', label: 'Ban Rate (%)', sortable: true }
      ],
      highRiskCitiesFields: [
        { key: 'city', label: 'City', sortable: true },
        { key: 'total_users', label: 'Total Users', sortable: true },
        { key: 'banned_users', label: 'Banned Users', sortable: true },
        { key: 'ban_rate', label: 'Ban Rate (%)', sortable: true }
      ],
      truefaceImageLoading: false,
      truefaceImageError: null,
      truefaceImageData: null,
      highRiskSignupIpsFields: [
        { key: 'signup_ip', label: 'Signup IP', sortable: true },
        { key: 'total_users', label: 'Total Users', sortable: true },
        { key: 'banned_users', label: 'Banned Users', sortable: true },
        { key: 'ban_rate', label: 'Ban Rate (%)', sortable: true }
      ],
      selectedBannedUsers: [],
      bannedUsersFields: [
        { key: 'user_id', label: 'User ID', sortable: true },
        { key: 'status', label: 'Status', sortable: true }
      ],
      currentSubnet: '',
      currentIpRange: '',
      currentCity: '',
      currentSignupIp: '',
      showEmailModal: false,
      emailModalValue: '',
      loadError: null
    }
  },
  computed: {
    hasAppLinks () {
      return Boolean(
        this.safeAppUrl(this.user.app_url) ||
        this.safeAppUrl(this.user.app_apple_app_store_url) ||
        this.safeAppUrl(this.user.app_google_play_url)
      )
    },
    hasAppSupport () {
      return Boolean(
        this.safeAppText(this.user.app_support_email) ||
        this.safeAppText(this.user.app_support_phone) ||
        this.safeAppUrl(this.user.app_support_url)
      )
    },
    offerPublisherPayoutSummary () {
      // Aggregated by the backend; the full event list is not in the response.
      const totals = this.user?.offer_publisher_payout_summary || {}

      return [
        {
          key: 'credited',
          label: 'Credited',
          textClass: 'text-success',
          value: Number(totals.credited) || 0
        },
        {
          key: 'pending',
          label: 'Pending',
          textClass: 'text-warning',
          value: Number(totals.pending) || 0
        },
        {
          key: 'earnings-jar',
          label: 'In Earnings Jar',
          textClass: 'text-info',
          value: Number(totals.earnings_jar) || 0
        },
        {
          key: 'disqualified',
          label: 'Disqualified',
          textClass: 'text-danger',
          value: Number(totals.disqualified) || 0
        }
      ]
    },
    offerActivityLinks () {
      return [
        { name: 'offer_events', label: 'Offer Events' },
        { name: 'offer_clicks', label: 'Offer Clicks' },
        { name: 'offer_user_iap_data', label: 'IAP Data' }
      ].filter((link) => this.canAccess(link.name))
    },
    isCurrentUserSupremeSuperAdminOrQA () {
      return this.canAccessPageByPermissionModules(
        ['internal.user.supreme_or_qa'],
        {
          enforceOnlyInProduction: true,
          allowTopAdmins: false
        }
      )
    },
    canViewPhoto () {
      return this.canAccessPageByPermissionModules(['user_details_view_photo'])
    },
    canUpdateEmail () {
      return this.canAccessPageByPermissionModules(['internal.user.update_email'])
    },
    userId () {
      return this.id ? this.id : this.$route.params.userId
    },
    isUserCountryHighRisk () {
      return !!this.user?.is_high_risk_country
    },
    emailDetails () {
      if (this.user.email) {
        const [name, domain] = this.user.email.split('@')
        return { ...this.user.email, name, domain }
      } else {
        return { ...this.user.email, name: null, domain: null }
      }
    },
    classSuspiciousResponseCode () {
      if (
        this.quality.suspicious_response_percentage > 2 &&
          this.quality.suspicious_response_count > 1
      ) return 'bg-danger text-white'
      if (this.quality.suspicious_response_percentage > 2) return 'bg-warning text-white'

      return 'bg-success text-white'
    },
    classAvgPublisherCPI () {
      if (this.quality?.click_additional_metrics && this.quality.click_additional_metrics.avg_cpi) {
        if (this.quality.click_additional_metrics.avg_cpi >= 1.3) return 'bg-danger text-white'
        if (this.quality.click_additional_metrics.avg_cpi >= 1) return 'bg-warning text-white'
        if (this.quality.click_additional_metrics.avg_cpi > 0) return 'bg-success text-white'

        return ''
      }

      return ''
    },
    classEarningPerClick () {
      if (this.performance?.total_clicks <= 3) return ''
      if (this.quality?.click_additional_metrics && this.quality.click_additional_metrics.earnings_per_click !== null) {
        if (this.quality.click_additional_metrics.earnings_per_click < 0.6) return 'bg-success text-white'
        if (this.quality.click_additional_metrics.earnings_per_click >= 0.6 && this.quality.click_additional_metrics.earnings_per_click <= 1) return 'bg-warning text-white'
        if (this.quality.click_additional_metrics.earnings_per_click > 1) return 'bg-danger text-white'
      }
      return ''
    },
    classTimeInCompletes () {
      if (this.performance?.total_clicks <= 3) return ''
      if (this.quality?.click_additional_metrics && this.quality.click_additional_metrics.time_in_completes_percentage !== null) {
        if (this.quality.click_additional_metrics.time_in_completes_percentage < 0.85) return 'bg-success text-white'
        if (this.quality.click_additional_metrics.time_in_completes_percentage >= 0.85 && this.quality.click_additional_metrics.time_in_completes_percentage <= 0.9) return 'bg-warning text-white'
        if (this.quality.click_additional_metrics.time_in_completes_percentage > 0.9) return 'bg-danger text-white'
      }
      return ''
    },
    classOverlappingCompletes () {
      if (this.quality?.overlap_rate) {
        const overlapRate = parseFloat(this.quality.overlap_rate)
        console.log(overlapRate, overlapRate > 0.25, 'overlapRate')
        if (overlapRate < 0.1) return 'bg-success text-white'
        if (overlapRate >= 0.1 && overlapRate <= 0.25) return 'bg-warning text-white'
        if (overlapRate > 0.25) return 'bg-danger text-white'
      }
      return ''
    },
    classTheSecondMedianWSLatency () {
      if (parseFloat(this.quality.the_second_median_ws_latency) < 100) return 'bg-success text-white'
      if (
        parseFloat(this.quality.the_second_median_ws_latency) >= 100 &&
          parseFloat(this.quality.the_second_median_ws_latency) <= 300
      ) return 'bg-warning text-white'
      if (parseFloat(this.quality.the_second_median_ws_latency) > 300) return 'bg-danger text-white'

      return ''
    },
    classTheSecondMedianTcpLatency () {
      if (parseFloat(this.quality.the_second_median_tcp_latency) < 100) return 'bg-success text-white'
      if (
        parseFloat(this.quality.the_second_median_tcp_latency) >= 100 &&
        parseFloat(this.quality.the_second_median_tcp_latency) <= 300
      ) return 'bg-warning text-white'
      if (parseFloat(this.quality.the_second_median_tcp_latency) > 300) return 'bg-danger text-white'

      return ''
    },
    classTheSecondMedianLatencyDelta () {
      const delta = parseFloat(this.quality.the_second_median_latency_delta)

      if (delta < 0) {
        return ''
      }

      if (delta < 40) {
        return 'bg-success text-white'
      }

      if (delta <= 60) {
        return 'bg-warning text-white'
      }

      return 'bg-danger text-white'
    },
    classTheSecondLatencyResults () {
      if (parseFloat(this.quality.the_second_latency_results) > 0.5) return 'bg-success text-white'
      if (
        parseFloat(this.quality.the_second_latency_results) >= 0.2 &&
          parseFloat(this.quality.the_second_latency_results) <= 0.5
      ) return 'bg-warning text-white'
      if (parseFloat(this.quality.the_second_latency_results) < 0.2) return 'bg-danger text-white'

      return ''
    },
    classDeviceOsMismatch () {
      if (parseFloat(this.quality.device_os_mismatch_percentage) < 0.1) return 'bg-success text-white'

      return 'bg-warning text-white'
    },
    classMaxTimeDelta () {
      if (parseFloat(this.quality.max_time_delta) <= 3610) return 'bg-success text-white'

      return 'bg-warning text-white'
    },
    classTimezoneMismatch () {
      const highRiskValues = [
        'Africa/Lagos',
        'America/Mexico_City',
        'America/Caracas',
        'Asia/Karachi',
        'Asia/Damascus',
        'Asia/Shanghai'
      ]

      if (
        this.quality.timezone_mismatch.some((timezoneMismatch) => highRiskValues.includes(timezoneMismatch)
        )
      ) {
        return 'bg-danger text-white'
      }

      return 'bg-warning text-white'
    },
    classCountryMismatch () {
      const highRiskValues = [
        'Nigeria',
        'Mexico',
        'Venezuela',
        'Pakistan',
        'Syria',
        'China'
      ]

      if (
        this.quality.country_mismatch.some((countryMismatch) => highRiskValues.includes(countryMismatch)
        )
      ) {
        return 'bg-danger text-white'
      }

      return 'bg-warning text-white'
    },
    classPatternRisk () {
      const pr = this.quality.pattern_risk
      if (!pr || !Object.values(pr).length || pr.total === null) return ''

      const totalPct = parseFloat(pr.banned_percentage || 0) * 100
      const pct24h   = parseFloat(pr.banned_percentage_24h || 0) * 100

      if (totalPct <= 10 && pct24h <= 10) return 'bg-success text-white'
      if (totalPct >= 25 || pct24h >= 25)  return 'bg-danger text-white'
      if (totalPct >= 10 || pct24h >= 10)  return 'bg-warning text-white'

      return ''
    },
    formattedPatternRiskText () {
      const pr = this.quality.pattern_risk
      if (!pr || !Object.values(pr).length) return 'No result'

      const total = pr.banned_percentage ?? 0
      const pct24h = pr.banned_percentage_24h ?? 0

      const totalStr = `${(total * 100).toFixed(1)} %`
      const pct24hStr = `${(pct24h * 100).toFixed(1)} %`

      let result = `${totalStr} (24h: ${pct24hStr})`

      if (
        pct24h !== null &&
        total * 100 <= 2 &&
        pct24h * 100 <= 2
      ) {
        result += ' ⭐'
      }

      return result
    },
    classSupplierBansQuality () {
      const supplierBansQuality =
          this.supplier_statistics?.filter(
            (supplier) => supplier.user_status === 'banned'
          ).length ?? 0

      if (supplierBansQuality === 1) return 'bg-warning text-white'
      if (supplierBansQuality >= 2) return 'bg-danger text-white'

      return 'bg-success text-white'
    },
    classRDPResult () {
      if (
        this.quality &&
          this.quality.ipqs_rdp_data &&
          this.quality.ipqs_rdp_data.rdp_usage
      ) {
        if (this.quality.ipqs_rdp_data.rdp_usage > 30) return 'bg-danger text-white'
        if (this.quality.ipqs_rdp_data.rdp_usage > 0) return 'bg-warning text-white'
        if (this.quality.ipqs_rdp_data.rdp_usage === 0) return 'bg-success text-white'
      }

      return ''
    },
    classIPQSScore () {
      if (
        this.quality &&
          this.quality.ipqs_rdp_data &&
          this.quality.ipqs_rdp_data.ipqs_unique_ips_avg
      ) {
        if (this.quality.ipqs_rdp_data.ipqs_unique_ips_avg >= 80) return 'bg-danger text-white'
        if (this.quality.ipqs_rdp_data.ipqs_unique_ips_avg > 50) return 'bg-warning text-white'
        if (this.quality.ipqs_rdp_data.ipqs_unique_ips_avg < 50) return 'bg-success text-white'
      }

      return ''
    },
    classHighRiskSubnets () {
      if (
        this.quality &&
        this.quality.high_risk_subnets &&
        this.quality.high_risk_subnets.length > 0
      ) {
        return 'bg-danger text-white'
      }
      return ''
    },
    classHighRiskIpRanges () {
      if (
        this.quality &&
        this.quality.high_risk_ip_ranges &&
        this.quality.high_risk_ip_ranges.length > 0
      ) {
        return 'bg-danger text-white'
      }
      return ''
    },
    classHighRiskCities () {
      if (
        this.quality &&
        this.quality.high_risk_cities &&
        this.quality.high_risk_cities.length > 0
      ) {
        return 'bg-danger text-white'
      }
      return ''
    },
    classHighRiskSignupIps () {
      if (
        this.quality &&
        this.quality.high_risk_signup_ips &&
        this.quality.high_risk_signup_ips.length > 0
      ) {
        return 'bg-danger text-white'
      }
      return ''
    },
    payoutAdjustmentValidate () {
      if (!this.userPayoutChanged) return null
      return (
        this.user.payout_adjustment >= 10 && this.user.payout_adjustment <= 100
      )
    },
    maxReconciliationBuyersValidate () {
      if (!this.userMaxReconciliationBuyers) return null
      return this.user.max_reconciliation_buyers <= 150
    },
    completeLimitValidate () {
      if (!this.completeLimitEnabled) return null
      return Number.isInteger(this.completeLimitValue) &&
          this.completeLimitValue >= 1 &&
          this.completeLimitValue <= 1000
    },
    completesAndConversionsChartOptions () {
      return {
        theme: {
          mode: this.$store.state.appConfig.layout.skin
        },
        chart: {
          height: 350,
          type: 'line'
        },
        stroke: {
          width: [0, 4]
        },
        dataLabels: {
          enabled: true,
          enabledOnSeries: [1]
        },
        tooltip: {
          x: {
            format: 'dddd, dd MMM'
          }
        },
        xaxis: {
          type: 'datetime',
          categories: this.completes_and_conversion_rates.labels
        },
        yaxis: [
          {
            title: {
              text: 'Completes'
            },
            labels: {
              formatter (val) {
                return val.toFixed(0)
              }
            }
          },
          {
            opposite: true,
            title: {
              text: 'Conversion Rate'
            }
          }
        ]
      }
    },
    publisherPayoutChartOptions () {
      return {
        theme: {
          mode: this.$store.state.appConfig.layout.skin
        },
        chart: {
          type: 'bar',
          stacked: true
        },
        colors: ['#20c997', '#ff69b4'], // Series 1: green teal, Series 2: pink
        dataLabels: {
          enabled: false
        },
        tooltip: {
          x: {
            format: 'dddd, dd MMM'
          },
          y: {
            formatter (val) {
              return (val < 0 ? '-$' : '$') + Math.abs(val).toFixed(2)
            }
          }
        },
        xaxis: {
          type: 'datetime',
          categories: this.publisherPayoutChart.labels
        },
        yaxis: [
          {
            labels: {
              formatter (val) {
                return val.toFixed(1)
              }
            }
          }
        ]
      }
    },
    classCompletesFactor () {
      // Show red background if there are alerts, regardless of total value
      if (this.completesFactor.total_alert > 0) {
        return 'bg-danger text-white'
      }

      // Apply background based on total value when no alerts are present
      if (this.completesFactor.total_actual <= 2) return '' // No color
      if (this.completesFactor.total <= 2) return 'bg-success text-white' // Green
      if (this.completesFactor.total > 2 && this.completesFactor.total <= 3) return 'bg-warning text-white' // Yellow
      if (this.completesFactor.total > 3) return 'bg-danger text-white' // Red
      return ''
    },

    breachesButtonText () {
      if (!this.emailBreaches || this.emailBreaches.count === 0) return 'Check Breaches'
      // Only row is NoBreach
      if (this.emailBreaches.count === 1 && this.emailBreaches.breach_list === 'NoBreach') {
        return 'No Breaches'
      }
      // Otherwise => X Breaches
      return `${this.emailBreaches.count} Breaches`
    },
    breachesButtonColor () {
      // No data => grey
      if (!this.emailBreaches || this.emailBreaches.count === 0) return 'secondary'
      if (this.emailBreaches.count === 1 && this.emailBreaches.breach_list === 'NoBreach') return 'danger'
      if (this.emailBreaches.count >= 3) return 'success'
      return 'warning'
    },
    connectedBadgeText () {
      if (!this.connectedAccounts || this.connectedAccounts.status === 'not_checked') return 'Check Connected Accounts'
      if (this.connectedAccounts.status === 'no_connected') return 'No Connected Accounts'
      return `${this.connectedAccounts.sum} Connected Accounts`
    },
    connectedButtonColor () {
      if (!this.connectedAccounts || this.connectedAccounts.status === 'not_checked') return 'secondary'
      if (this.connectedAccounts.status === 'no_connected') return 'danger'
      if (this.connectedAccounts.sum <= 2) return 'warning'
      return 'success'
    },
    emailAgeText () {
      if (!this.emailBreaches || this.emailBreaches.count === 0) return 'Unknown'
      if (this.emailBreaches.count === 1 && this.emailBreaches.breach_list === 'NoBreach') return 'Unknown'
      // otherwise, we have a numeric email_age
      if (this.emailBreaches.email_age !== null) {
        return `${this.emailBreaches.email_age} old`
      }
      return 'Unknown'
    },
    emailAgeColorClass () {
      // If unknown => orange
      if (!this.emailBreaches || this.emailBreaches.count === 0) return 'warning'
      if (this.emailBreaches.count === 1 && this.emailBreaches.breach_list === 'NoBreach') return 'warning'

      if (this.emailBreaches.email_age) {
        // otherwise => we have a string email_age like "2 Years" or "5 Months"
        const yrs = parseInt(this.emailBreaches.email_age, 10)
        if (this.emailBreaches.email_age.includes('Years') && yrs >= 2) return 'success'
      }
      return 'warning'
    },
    limitedConnectedAccounts () {
      if (!this.relatedAccounts || !Array.isArray(this.relatedAccounts.results)) {
        return []
      }
      const sorted = [...this.relatedAccounts.results].sort((a, b) => {
        return (b.is_pinned ? 1 : 0) - (a.is_pinned ? 1 : 0)
      })
      return sorted.slice(0, 6)
    },
    hasRelatedAccountsSummary () {
      return !!(this.relatedAccounts && this.relatedAccounts.summary)
    },
    modalRelatedAccountsResults () {
      if (!this.relatedAccountsModalData || !Array.isArray(this.relatedAccountsModalData.results)) {
        return []
      }
      return this.relatedAccountsModalData.results
    },
    shouldShowSameOffersTaken () {
      return !!this.relatedAccounts?.has_same_offers_taken
    },
    filteredSupplierStatistics () {
      if (this.showSuppliersWithoutClicks) {
        return this.supplier_statistics
      }
      return this.supplier_statistics.filter(s => Number(s.total_clicks || 0) > 0)
    },
    emailActionLabel () {
      return this.user && this.user.email ? 'Edit Email' : 'Set Email'
    },
    isEmailValid () {
      const value = (this.emailModalValue || '').trim()
      if (!value) return false
      const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return emailRegex.test(value.toLowerCase())
    }
  },
  methods: {
    safeAppUrl (value) {
      if (typeof value !== 'string') return null

      const trimmed = value.trim()

      try {
        const url = new URL(trimmed)

        return ['http:', 'https:'].includes(url.protocol) && url.hostname ? trimmed : null
      } catch (error) {
        return null
      }
    },
    safeAppText (value) {
      if (typeof value !== 'string') return null

      const trimmed = value.trim()

      return trimmed === '' ? null : trimmed
    },
    loadDefaultRelatedAccountsPreview () {
      if (!this.userId) return
      if (this.relatedAccounts && Array.isArray(this.relatedAccounts.results) && this.relatedAccounts.results.length > 0) return

      this.$http.get(`/api/user/${this.userId}/related-accounts`, {
        params: { days: DEFAULT_RELATED_ACCOUNT_DAYS }
      })
        .then((response) => {
          this.relatedAccounts = response?.data?.data || {}
        })
        .catch((error) => {
          console.error('Error loading related accounts preview', error)
        })
    },
    openRelatedAccountsModal (days = DEFAULT_RELATED_ACCOUNT_DAYS) {
      this.selectedRelatedAccountsDays = days
      this.$refs.user_related_account_data.show()
      this.fetchRelatedAccounts(days)
    },
    changeRelatedAccountsDays (days) {
      if (days === this.selectedRelatedAccountsDays && this.relatedAccountsModalData) return

      this.selectedRelatedAccountsDays = days
      this.fetchRelatedAccounts(days)
    },
    async fetchRelatedAccounts (days = this.selectedRelatedAccountsDays) {
      if (!this.userId) return

      const requestId = ++this.relatedAccountsModalRequestId
      this.isRelatedAccountsModalLoading = true
      this.relatedAccountsModalError = null

      try {
        const response = await this.$http.get(`/api/user/${this.userId}/related-accounts`, {
          params: { days }
        })

        if (requestId !== this.relatedAccountsModalRequestId) return

        this.relatedAccountsModalData = response?.data?.data || null
      } catch (error) {
        if (requestId !== this.relatedAccountsModalRequestId) return

        this.relatedAccountsModalData = null
        this.relatedAccountsModalError = error?.response?.data?.message || 'Failed to load related accounts.'
        console.error('Error loading related accounts', error)
      } finally {
        if (requestId === this.relatedAccountsModalRequestId) {
          this.isRelatedAccountsModalLoading = false
        }
      }
    },
    onConnectedAccountPinned ({ connectedUserId }) {
      this.patchConnectedAccountPin(connectedUserId, { is_pinned: true })
    },
    onConnectedAccountUnpinned ({ connectedUserId }) {
      this.dropPinnedOnly(connectedUserId)
      this.patchConnectedAccountPin(connectedUserId, { is_pinned: false, is_pinned_only: false })
    },
    async togglePreviewPin (account) {
      if (this.pinPendingIds.includes(account.user_id)) return
      this.pinPendingIds.push(account.user_id)
      try {
        if (account.is_pinned) {
          await this.$http.delete(`/api/user/${this.userId}/related-accounts/pin/${account.user_id}`)
          this.onConnectedAccountUnpinned({ connectedUserId: account.user_id })
          this.alertSuccess('Connected account unpinned')
        } else {
          await this.$http.post(`/api/user/${this.userId}/related-accounts/pin`, {
            connected_user_id: account.user_id
          })
          this.onConnectedAccountPinned({ connectedUserId: account.user_id })
          this.alertSuccess('Connected account pinned')
        }
      } catch (errors) {
        if (errors && errors.response && errors.response.data) {
          this.alertError(errors.response.data.errors)
        }
      } finally {
        this.pinPendingIds = this.pinPendingIds.filter(id => id !== account.user_id)
      }
    },
    patchConnectedAccountPin (connectedUserId, patch) {
      const apply = (row) => (row.user_id === connectedUserId ? { ...row, ...patch } : row)
      if (this.relatedAccountsModalData?.results) {
        this.relatedAccountsModalData = {
          ...this.relatedAccountsModalData,
          results: this.relatedAccountsModalData.results.map(apply)
        }
      }
      if (this.relatedAccounts?.results) {
        this.relatedAccounts = {
          ...this.relatedAccounts,
          results: this.relatedAccounts.results.map(apply)
        }
      }
    },
    dropPinnedOnly (connectedUserId) {
      const drop = (rows) => rows.filter(r => !(r.user_id === connectedUserId && r.is_pinned_only))
      if (this.relatedAccountsModalData?.results) {
        this.relatedAccountsModalData = {
          ...this.relatedAccountsModalData,
          results: drop(this.relatedAccountsModalData.results)
        }
      }
      if (this.relatedAccounts?.results) {
        this.relatedAccounts = {
          ...this.relatedAccounts,
          results: drop(this.relatedAccounts.results)
        }
      }
    },
    handleClickBreaches () {
      if (!this.user.hash) return

      if (!this.emailBreaches || this.emailBreaches.count === 0) {
        this.showLoading()

        this.$http.get(`/api/user/${this.user.hash}/check-breaches`)
          .then(response => {
            if (response.data) {
              this.emailBreaches = response.data
            }
          })
          .catch(err => {
            console.error('Error checking breaches', err)
          })
          .finally(() => {
            this.hideLoading()
          })
      } else {
        this.$refs.breachesModal.show()
      }
    },

    handleClickAccountsConnected () {
      if (!this.user.hash) return

      if (!this.connectedAccounts || this.connectedAccounts.status === 'not_checked') {
        this.showLoading()

        this.$http.get(`/api/user/${this.user.hash}/check-connected-accounts`)
          .then(response => {
            if (response.data) {
              this.connectedAccounts = response.data
            }
          })
          .catch(err => {
            console.error('Error checking connected accounts', err)
          })
          .finally(() => {
            this.hideLoading()
          })
      } else {
        this.$refs.accountsConnectedModal.show()
      }
    },
    parseArray (data) {
      if (Array.isArray(data)) {
        return data
      } else if (typeof data === 'object' && data !== null) {
        return Object.values(data)
      }
      return []
    },
    showProtectedStatusModal () {
      this.getProtectedUserSTatusModalVue().showForm()
    },
    changeStatus (fieldName) {
      this.showLoading()
      this.$http
        .post(`/api/user/${this.userId}/setting-status`, {
          field: fieldName,
          status: this.user[fieldName]
        })
        .then(() => {
          this.alertSuccess(`${fieldName} has been updated!`)
          this.hideLoading()
        })
        .catch((errors) => {
          this.hideLoading()
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })

      this.$emit('update-user-settings', { field: fieldName, value: this.user[fieldName] })
    },
    offerStatusStartEditing () {
      this.offerStatusPrev = Number(this.user.offers_status)
      this.offerStatusEditing = true
    },
    offerStatusApply () {
      // call your existing generic updater unchanged
      this.changeStatus('offers_status')
      this.offerStatusEditing = false
    },
    offerStatusCancel () {
      this.user.offers_status = this.offerStatusPrev
      this.offerStatusEditing = false
    },
    offerStatusVariant (val) {
      const n = Number(val)
      if (n === 1) return 'success'
      if (n === 2) return 'danger'
      return 'secondary'
    },
    offerStatusExtraClass (val) {
      return Number(val) === 3 ? 'user-status-3' : ''
    },
    offerStatusLabel (val) {
      const n = Number(val)
      if (n === 1) return 'Active'
      if (n === 2) return 'Inactive'
      if (n === 3) return 'Shadow-banned'
      return 'Unknown'
    },
    addedSupplierUserBanned (data) {
      for (let i = 0; i < this.supplier_statistics.length; i++) {
        if (this.supplier_statistics[i].id === data.form.id) {
          this.supplier_statistics[i].user_status =
              data.form.type === 5 ? 'banned' : 'disabled'
          this.supplier_statistics[i].banned_reasons = data.form.comment
          this.supplier_statistics[i].banned_until = data.form.ban_indefinite ? null : this.ymdDateStrToTZDateStr(data.form.ban_expiry)
        }
      }
    },
    getSupplierBanFormVue () {
      return this.$refs.supplier_ban_form
    },
    showListUserBanModal (item) {
      this.getListUserBanModalVue().showModal(item)
    },
    showResetHashesModal (item) {
      this.getResetHashesModalVue().showModal(item)
    },
    getListUserBanModalVue () {
      return this.$refs.list_user_ban_modal
    },
    getResetHashesModalVue () {
      return this.$refs.reset_hashes_modal
    },
    showUserBuyerBans (item) {
      this.$http
        .get(`/api/user/${this.userId}/buyer-bans/${item.id}`)
        .then((response) => {
          this.$refs['user_buyer_bans_modal'].show()
          this.currentBuyerBans = response.data
        })
        .catch((errors) => {
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
    },
    showSimilarUserProfiling (userProfilingId) {
      this.showLoading()

      this.$http
        .get(`/api/user-profiling/${userProfilingId}/similar`)
        .then((response) => {
          this.$refs['similar_user_profiling_modal'].show()
          this.similarUserProfilings = response.data.data
        })
        .catch((errors) => {
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
        .finally(() => {
          this.hideLoading()
        })
    },
    showFraudEmailDomains (emailDomain) {
      this.$http
        .get('/api/user-profiling/fraud-email-domains', {
          params: {
            emailDomain
          }
        })
        .then((response) => {
          this.$refs['user_domain_fraud'].show()
          this.emailDomainFrauds = response.data.data
        })
        .catch((errors) => {
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
    },
    showUserProfilingBasics (userId) {
      this.$http
        .get(`/api/user-profiling/${userId}/basics`)
        .then((response) => {
          this.$refs['user_profiling_basics_modal'].show()
          this.userProfilingBasics = response.data.data
        })
        .catch((errors) => {
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
    },
    showUsersByCarrier (carrier) {
      this.currentCarrier = carrier
      this.$refs['usersByCarrier'].show()
    },
    getTruefaceStatusVariant (status) {
      if (status === 0) return 'danger'
      if (status === 1) return 'warning'
      if (status === 2) return 'success'
      return 'secondary'
    },
    getTruefaceStatusText (status) {
      if (status === 0) return 'Not Verified'
      if (status === 1) return 'Should be Verified'
      if (status === 2) return 'Verified'
      return 'Unknown'
    },
    redirectToTruefaceReview () {
      this.$router.push({
        path: '/trueface-user-verifications',
        query: { 'filter[user_id]': this.user.id }
      })
    },
    async showTruefaceImageModal () {
      this.truefaceImageData = null
      this.truefaceImageLoading = true
      this.truefaceImageError = null
      this.$refs.truefaceImageModal.show()

      try {
        const response = await this.$http.get(`/api/trueface/user-verifications/image/${this.user.app_id}/${this.user.id}`)

        if (response.data && response.data.image) {
          const base64Data = response.data.image
          let mimeType = 'image/jpeg'

          if (base64Data.startsWith('iVBORw0KGgo')) {
            mimeType = 'image/png'
          } else if (base64Data.startsWith('R0lGOD')) {
            mimeType = 'image/gif'
          } else if (base64Data.startsWith('UklGR')) {
            mimeType = 'image/webp'
          }

          this.truefaceImageData = `data:${mimeType};base64,${base64Data}`
        } else {
          this.truefaceImageData = null
        }
      } catch (error) {
        this.alertError('Failed to load image')
        this.truefaceImageError = 'Failed to load image'
      } finally {
        this.truefaceImageLoading = false
      }
    },
    getProtectedUserSTatusModalVue () {
      return this.$refs.protected_user_status
    },
    changeToReviewStatus (data) {
      this.can_trigger_verification = false
      this.phone_verifications = [
        {phone: null, verified: false},
        ...this.phone_verifications
      ]
      this.user = data.response
    },
    activatedUser (response) {
      this.user = response
    },
    async activateSupplierAgain (supplier) {
      if (
        confirm(
          'Are you sure you want to activate the supplier again for this user?'
        )
      ) {
        const supplierId = supplier.id
        this.showLoading()

        if (supplier.is_disabled_by_user) {
          await this.reactivateSupplier(supplierId)

          if (supplier.user_status === 'disabled') {
            await this.unBanSupplier(supplierId)
          }
        } else {
          await this.unBanSupplier(supplierId)
        }

        this.hideLoading()
      }
    },
    reactivateSupplier (supplierId) {
      this.$http
        .patch(`/api/user/${this.userId}/supplier/${supplierId}/activate`)
        .then((response) => {
          this.supplier_statistics = response.data.supplier_statistics
        })
        .catch((errors) => {
          this.alertError(errors.response.data)
        })
    },
    unBanSupplier (supplierId) {
      this.$http
        .delete(`/api/user/${this.userId}/ban/${supplierId}`)
        .then((response) => {
          this.supplier_statistics = response.data.supplier_statistics
        })
        .catch((errors) => {
          this.alertError(errors.response.data)
        })
    },
    showConfirmResetBirthdate () {
      if (confirm('Are you sure you want to reset birthdate this user?')) {
        this.$http
          .put(`/api/user/${this.userId}/reset-birthdate`)
          .then(() => {
            this.hideLoading()
            this.user.birthday = null
            this.alertSuccess('Birthdate has been reset')
          })
          .catch((errors) => {
            this.alertError(errors.response.data)
            this.hideLoading()
          })
      }
    },
    autoSaveComment () {
      if (this.user.org_comment === this.user.comment) return
      this.$http
        .post(`/api/user/${this.userId}/comment`, {
          comment: this.user.comment
        })
        .then((response) => {
          this.user.org_comment = this.user.comment
          this.user.comment_updated_at = response.data.comment_updated_at
          this.user.comment_updated_admin_id =
                response.data.comment_updated_admin_id
          this.user.comment_admin_name = response.data.commented_admin.name
          this.user.comment_admin_hex_color =
                response.data.commented_admin.hex_color
        })
        .catch((errors) => {
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
    },
    updateCountry (user, selectedOption) {
      user.country = selectedOption.code
      user.country_name = selectedOption.name
      this.getUserDetails()
    },
    updateUserLanguages () {
      this.$http
        .put(`/api/user/${this.userId}/languages`, {
          languages: this.user.languages.map((language) => {
            return {language: language.code}
          })
        })
        .then(() => {
          this.showEditLanguage = false
          this.alertSuccess('Languages has been update')
        })
        .catch((errors) => {
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
    },
    updateUserInterfaceLanguage () {
      this.$http
        .post(`/api/user/${this.userId}/interface-language/${this.userInterfaceLanguage.code}`)
        .then(() => {
          this.user.interface_language = this.userInterfaceLanguage.code
          this.showEditInterfaceLanguage = false
          this.alertSuccess('Interface Language has been update')
        })
        .catch((errors) => {
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
    },
    updatePayoutAdjustment (value) {
      this.$http
        .put(`/api/user/${this.userId}/payout-adjustment`, {
          payout_adjustment: value
        })
        .then(() => {
          this.alertSuccess('Payout Adjustment has been update')
        })
        .catch((errors) => {
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
    },
    updateMinPublisherPayout (value) {
      this.$http
        .put(`/api/user/${this.userId}/min-publisher-payout`, {
          min_publisher_payout: value
        })
        .then(() => {
          this.alertSuccess('Min Publisher Payout has been update')
        })
        .catch((errors) => {
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
    },
    updateMaxReconciliationBuyers (value) {
      this.$http
        .put(`/api/user/${this.userId}/max-reconciliation-buyers`, {
          max_reconciliation_buyers: value
        })
        .then(() => {
          this.alertSuccess('Max Reconciliation Buyers has been update')
        })
        .catch((errors) => {
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
    },
    syncCompleteLimitControls () {
      const limit = this.user ? this.user.complete_limit : null

      this.completeLimitEnabled = !!limit && !limit.triggered_at

      if (limit) {
        this.completeLimitValue = limit.limit_completes
      }
    },
    toggleCompleteLimit (enabled) {
      if (!enabled) {
        this.removeCompleteLimit()

        return
      }

      if (!this.completeLimitValidate) {
        const stored = this.user.complete_limit

        this.completeLimitValue = stored ? stored.limit_completes : 5
      }

      this.saveCompleteLimit()
    },
    saveCompleteLimit () {
      if (!this.completeLimitValidate) {
        return
      }

      this.completeLimitSaving = true
      this.$http
        .put(`/api/user/${this.userId}/complete-limit`, {
          limit_completes: this.completeLimitValue
        })
        .then((response) => {
          this.$set(this.user, 'complete_limit', response.data)
          this.alertSuccess('Limit Completes has been updated')
        })
        .catch((errors) => {
          this.syncCompleteLimitControls()
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
        .finally(() => {
          this.completeLimitSaving = false
        })
    },
    removeCompleteLimit () {
      this.completeLimitSaving = true
      this.$http
        .delete(`/api/user/${this.userId}/complete-limit`)
        .then(() => {
          this.$set(this.user, 'complete_limit', null)
          this.alertSuccess('Limit Completes has been removed')
        })
        .catch((errors) => {
          this.syncCompleteLimitControls()
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
        .finally(() => {
          this.completeLimitSaving = false
        })
    },
    getClassVerificationDelay (delay) {
      const minutes = delay / 60

      if (minutes < 5) return 'bg-success text-white'
      if (minutes < 10) return 'bg-warning text-white'
      if (minutes >= 10) return 'bg-danger text-white'
    },
    getClassIpqsScore (score) {
      if (parseFloat(score) >= 80) return 'bg-danger text-white'
      if (parseFloat(score) > 50) return 'bg-warning text-white'
      if (parseFloat(score) < 50) return 'bg-success text-white'
    },
    getClassCarrierRisk (bannedPercentage) {
      const percent = Math.round(parseFloat(bannedPercentage) * 100)

      if (percent >= 60) return 'bg-danger text-white'
      if (percent >= 25 && percent < 60) return 'bg-warning text-white'

      return 'bg-success text-white'
    },
    getClassReconciliationPercentage (percentage) {
      if (!percentage || percentage.toFixed() < 5) return 'bg-success text-white'
      if (percentage.toFixed() > 10) return 'bg-danger text-white'

      return 'bg-warning text-white'
    },
    getClassBannedUserDuplicates (openEnd) {
      if (parseFloat(openEnd.banned_users_count_percentage) < 0.05) return 'bg-success text-white'
      if (
        parseFloat(openEnd.banned_users_count_percentage) >= 0.05 &&
          parseFloat(openEnd.banned_users_count_percentage) <= 0.2
      ) return 'bg-warning text-white'
      if (parseFloat(openEnd.banned_users_count_percentage) > 0.2) return 'bg-danger text-white'

      return ''
    },
    getConfidence (avgDaysString) {
      const avgDays = parseInt(avgDaysString)

      let text = null, cssClass = null

      if (avgDays >= 10) {
        text = 'High'
        cssClass = 'bg-success text-white'
      } else if (avgDays >= 7) {
        text = 'Medium'
        cssClass = 'bg-warning text-white'
      } else {
        text = 'Low'
        cssClass = 'bg-warning text-white'
      }

      return { text, cssClass }
    },
    getProfitabilityScoreValue () {
      const offerPayout = parseFloat(this.user.offer_publisher_payout || 0)
      const iapSpend = parseFloat(this.user.iap_spend || 0)

      if (offerPayout === 0) {
        return null
      }

      if (iapSpend === 0) {
        return 0
      }

      return Math.round((iapSpend / offerPayout) * 10) / 10
    },
    getProfitabilityScoreDisplay () {
      const score = this.getProfitabilityScoreValue()

      if (score === null) {
        return '-'
      }

      return score.toString()
    },
    getProfitabilityScoreClass () {
      const score = this.getProfitabilityScoreValue()
      const offerPayout = parseFloat(this.user.offer_publisher_payout || 0)
      const iapSpend = parseFloat(this.user.iap_spend || 0)

      if (offerPayout === 0) {
        return ''
      }

      if (offerPayout > 0 && iapSpend === 0) {
        return 'bg-danger text-white'
      }

      if (score !== null && score > 0) {
        if (score < 0.8) {
          return 'bg-danger text-white'
        } else if (score >= 0.8 && score < 1) {
          return 'bg-light-danger text-dark'
        } else if (score >= 1 && score <= 1.5) {
          return 'bg-light-success text-dark'
        } else if (score > 1.5) {
          return 'bg-success text-white'
        }
      }

      return ''
    },
    getProfitabilityScoreStars () {
      const score = this.getProfitabilityScoreValue()
      const iapSpend = parseFloat(this.user.iap_spend || 0)

      if (iapSpend <= 50 || score === null || score <= 1.5) {
        return 0
      }

      if (score > 5) {
        return 3
      } else if (score > 2.5) {
        return 2
      } else if (score > 1.5) {
        return 1
      }

      return 0
    },
    resetUserDataOnSupplier (supplierId) {
      if (
        confirm(
          'Only proceed if the user has great reconciliations and overall good account standing. Only proceed if the user has confirmed he deleted his cookies.'
        )
      ) {
        this.$http
          .post(`/api/user/${this.userId}/reset-supplier/${supplierId}`)
          .then((response) => {
            this.hideLoading()
            this.supplier_statistics = response.data.supplier_statistics
          })
          .catch((errors) => {
            this.alertError(errors.response.data)
            this.hideLoading()
          })
      }
    },
    getUserDetails () {
      const currentUserDetails =
          this.$store.getters['user/getCurrentUserDetails']

      this.loadError = null

      // If the user details exist in the store, use them
      // eslint-disable-next-line eqeqeq
      if (currentUserDetails && currentUserDetails.user.id == this.userId) {
        this.performance = currentUserDetails.performance
        this.quality = currentUserDetails.quality
        this.quality_questions = currentUserDetails.quality_questions
        this.supplier_statistics = currentUserDetails.supplier_statistics
        this.user = currentUserDetails.user
        this.user.org_comment = currentUserDetails.user.comment
        this.phone_verifications = currentUserDetails.phone_verifications
        this.can_trigger_verification =
            currentUserDetails.can_trigger_verification
        this.can_require_facetec =
            currentUserDetails.can_require_facetec
        this.reconciliation = currentUserDetails.reconciliation
        this.completes_and_conversion_rates =
            currentUserDetails.completes_and_conversion_rates_thirty_days_ago
        this.sourceExtensionLogCount = currentUserDetails.extension_logs_count
        this.otherAccounts = currentUserDetails.other_accounts
        this.relatedAccounts = currentUserDetails.related_accounts
        this.languageCodes = currentUserDetails.language_codes
        this.isHighRiskEmailDomain = currentUserDetails.is_high_risk_email_domain
        this.platformLastHundredClicks = currentUserDetails.platform_last_100_clicks
        this.staffReviewer = currentUserDetails.staff_reviewer
        this.completesFactor = currentUserDetails.completes_factor
        this.publisherPayoutChart = currentUserDetails.daily_payout_chart
        this.emailBreaches = currentUserDetails.email_breaches
        this.connectedAccounts = currentUserDetails.connected_accounts

        this.loadDefaultRelatedAccountsPreview()

        // Prefetch offers overview if toggle is enabled (when user details are already in store)
        this.prefetchOffersOverview()
      } else {
        if (this.hideFullPageSpinner) {
          this.isLoadingDetails = true
        } else {
          this.showLoading()
        }

        this.$http
          .get(`/api/user/${this.userId}`)
          .then((response) => {
            if (this.hideFullPageSpinner) {
              this.isLoadingDetails = false
            } else {
              this.hideLoading()
            }
            const returnData = response.data

            this.performance = returnData.performance
            this.quality = returnData.quality
            this.quality_questions = returnData.quality_questions
            this.supplier_statistics = returnData.supplier_statistics
            this.user = returnData.user
            this.user.org_comment = this.user.comment
            this.phone_verifications = returnData.phone_verifications
            this.can_trigger_verification = returnData.can_trigger_verification
            this.can_require_facetec = returnData.can_require_facetec
            this.reconciliation = returnData.reconciliation
            this.completes_and_conversion_rates =
                  returnData.completes_and_conversion_rates_thirty_days_ago
            this.sourceExtensionLogCount = returnData.extension_logs_count
            this.otherAccounts = returnData.other_accounts
            this.relatedAccounts = returnData.related_accounts
            this.languageCodes = returnData.language_codes
            this.isHighRiskEmailDomain = returnData.is_high_risk_email_domain
            this.platformLastHundredClicks = returnData.platform_last_100_clicks
            this.staffReviewer = returnData.staff_reviewer
            this.completesFactor = returnData.completes_factor
            this.publisherPayoutChart = returnData.daily_payout_chart
            this.emailBreaches = returnData.email_breaches
            this.connectedAccounts = returnData.connected_accounts

            this.loadDefaultRelatedAccountsPreview()

            // Save the user details in the Vuex store
            this.$store.commit('user/SET_CURRENT_USER_DETAIL', returnData)

            // Prefetch offers overview if toggle is enabled
            this.prefetchOffersOverview()
          })
          .catch((errors) => {
            this.handleUserDetailsError(errors)
            if (this.hideFullPageSpinner) {
              this.isLoadingDetails = false
            } else {
              this.hideLoading()
            }
          })
      }
    },
    // A dead backend yields no CORS headers, so axios reports no `response` at all.
    handleUserDetailsError (errors) {
      const responseData = errors?.response?.data
      const status = errors?.response?.status
      let message = 'Could not load this user profile. The server did not return a response.'

      if (status) {
        message = `Could not load this user profile (error ${status}). Please try again.`
      }

      // Only take the page over when there is nothing already on screen.
      if (!this.user) {
        this.loadError = message
      }

      this.alertError(responseData || errors?.message || message)
    },
    refreshUserDetails () {
      this.can_require_facetec = false
      if (this.quality) {
        this.quality.trueface_status = 1
      }
      this.showLoading()
      this.$http
        .get(`/api/user/${this.userId}`)
        .then((response) => {
          this.hideLoading()
          const returnData = response.data
          this.performance = returnData.performance
          this.quality = returnData.quality
          this.user = returnData.user
          this.user.org_comment = this.user.comment
          this.phone_verifications = returnData.phone_verifications
          this.can_trigger_verification = returnData.can_trigger_verification
          this.can_require_facetec = returnData.can_require_facetec
          this.$store.commit('user/SET_CURRENT_USER_DETAIL', returnData)
        })
        .catch((errors) => {
          this.hideLoading()
          this.handleUserDetailsError(errors)
        })
    },
    prefetchOffersOverview () {
      // Check if preload toggle is enabled (from prop or route query)
      const isPreloadEnabled = this.preloadOffersOverview ||
                               this.$route.query.preloadOffersOverview === 'true'
      if (!isPreloadEnabled) {
        return
      }

      // Check if prefetch is already in progress
      if (this.$store.getters['user/isPrefetchingOffersOverview'](this.userId)) {
        return // Prefetch already in progress
      }

      // Mark prefetch as in progress
      this.$store.commit('user/SET_PREFETCHING_OFFERS_OVERVIEW', { userId: this.userId, isPrefetching: true })

      // Prefetch offers overview data in the background
      const prefetchPromise = this.$http
        .get(`/api/user/${this.userId}/offer-details`)
        .then((response) => {
          // Store prefetched data in Vuex store
          this.$store.commit('user/SET_PREFETCHED_OFFERS_OVERVIEW', {
            userId: this.userId,
            data: response.data.data
          })
          return response.data.data
        })
        .catch(() => {
          // Silently fail - this is a background prefetch, don't show errors
          return null
        })
        .finally(() => {
          // Remove prefetching flag
          this.$store.commit('user/SET_PREFETCHING_OFFERS_OVERVIEW', { userId: this.userId, isPrefetching: false })
          // Remove promise reference
          this.$store.commit('user/SET_PREFETCH_PROMISE', { userId: this.userId, promise: null })
        })

      // Store promise reference in Vuex store so other components can await it
      this.$store.commit('user/SET_PREFETCH_PROMISE', { userId: this.userId, promise: prefetchPromise })

      // Clean up promise after 5 minutes
      setTimeout(() => {
        this.$store.commit('user/SET_PREFETCH_PROMISE', { userId: this.userId, promise: null })
      }, 5 * 60 * 1000)
    },
    getSupplierFactorClass (item) {
      if (item.actual_completes <= 2) return '' // No color if completes <= 2
      if (item.factor <= 2) return 'bg-success text-white' // Green if factor <= 2
      if (item.factor > 2 && item.factor <= 3) return 'bg-warning text-white' // Yellow if factor > 2 and <= 3
      if (item.factor > 3) return 'bg-danger text-white' // Red if factor > 3
      return '' // Default no color
    },
    setQuickLinkProperties () {
      store.commit('app/SET_QUICK_LINK_PROPERTIES', {
        display: true,
        id: this.id,
        project: 'ps',
        model: 'user'
      })
    },
    showSuspiciousUserProfilingModal () {
      this.$refs.highRiskUserProfilingDetails.show()
    },
    showHighRiskSubnetsModal () {
      this.$refs.highRiskSubnetsModal.show()
    },
    showHighRiskIpRangesModal () {
      this.$refs.highRiskIpRangesModal.show()
    },
    showHighRiskCitiesModal () {
      this.$refs.highRiskCitiesModal.show()
    },
    showHighRiskSignupIpsModal () {
      this.$refs.highRiskSignupIpsModal.show()
    },
    showClaimAccessCountryMismatchModal () {
      this.$refs.claimAccessCountryMismatchModal.show()
    },
    getClaimAccessCountryCode (country) {
      return typeof country === 'string' ? country : country.code
    },
    getClaimAccessCountryName (country) {
      return typeof country === 'string' ? country : country.name || country.code
    },
    showBannedUsersModal (users, subnet) {
      this.selectedBannedUsers = users || []
      this.currentSubnet = subnet || ''
      this.currentIpRange = ''
      this.currentCity = ''
      this.currentSignupIp = ''
      this.$refs.highRiskSubnetsModal.hide()
      this.$refs.bannedUsersModal.show()
    },
    showBannedUsersModalForRange (users, ipRange) {
      this.selectedBannedUsers = users || []
      this.currentIpRange = ipRange || ''
      this.currentSubnet = ''
      this.currentCity = ''
      this.currentSignupIp = ''
      this.$refs.highRiskIpRangesModal.hide()
      this.$refs.bannedUsersModal.show()
    },
    showBannedUsersModalForCity (users, city) {
      this.selectedBannedUsers = users || []
      this.currentCity = city || ''
      this.currentSubnet = ''
      this.currentIpRange = ''
      this.currentSignupIp = ''
      this.$refs.highRiskCitiesModal.hide()
      this.$refs.bannedUsersModal.show()
    },
    showBannedUsersModalForSignupIp (users, signupIp) {
      this.selectedBannedUsers = users || []
      this.currentSignupIp = signupIp || ''
      this.currentSubnet = ''
      this.currentIpRange = ''
      this.currentCity = ''
      this.$refs.highRiskSignupIpsModal.hide()
      this.$refs.bannedUsersModal.show()
    },
    showIapDataModal () {
      this.showLoading()

      this.$http
        .get(`/api/user/${this.userId}/iap-data-group-offer`)
        .then((response) => {
          this.hideLoading()
          this.$refs['iapDataModal'].show()
          this.iapData = response.data.data
        })
        .catch((errors) => {
          this.hideLoading()
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
    },
    showSameOffersTakenModal () {
      this.showLoading()

      this.$http
        .get(`/api/user/${this.userId}/same-offers-taken`)
        .then((response) => {
          this.hideLoading()
          this.$refs['sameOffersTakenModal'].show()
          this.sameOffersTaken = response.data.data
        })
        .catch((errors) => {
          this.hideLoading()
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
    },
    openPatternRiskModal () {
      // Set the header info
      this.patternRiskParams.app_id = this.user.app_id
      this.patternRiskParams.country_code = this.user.country
      this.patternRiskParams.external_source = this.user.external_source

      this.showLoading()

      this.$http.get(`/api/user/${this.user.id}/pattern-risk-users`, {
        params: this.patternRiskParams}
      )
        .then(res => {
          this.patternRiskList = res.data
          this.$refs.patternRiskModal.show()
        })
        .catch(() => {
          this.patternRiskList = []
          this.$refs.patternRiskModal.show()
        })
        .finally(() => {
          this.hideLoading()
        })
    },

    /**
     * Trigger loading of lifetime reconciliation on demand
     */
    async getLifetimeReconciliations () {
      if (this.lifetimeReconciliation.length === 0) {
        this.isLoadingLifetime = true
        try {
          const {
            data: { lifetime_reconciliation }
          } = await this.$http.get(
            `/api/user/${this.userId}/lifetime-reconciliations`
          )
          this.lifetimeReconciliation = lifetime_reconciliation
        } catch (error) {
          console.error('Failed to load lifetime reconciliation', error)
        } finally {
          this.isLoadingLifetime = false
        }

        // Calculate total lifetime reconciliation %
        const totalCompletes = this.lifetimeReconciliation.reduce(
          (sum, item) => sum + item.completes_count,
          0
        )
        const totalReconciled = this.lifetimeReconciliation.reduce(
          (sum, item) => sum + item.reconciled_count,
          0
        )
        this.totalLifetimePercentage = totalCompletes ? ((totalReconciled / totalCompletes) * 100).toFixed(2) : '0'
      }
    },
    openEmailModal () {
      this.emailModalValue = (this.user && this.user.email) || ''
      this.showEmailModal = true
    },
    submitEmail () {
      const email = this.emailModalValue.trim()
      this.$http.put(`/api/user/${this.userId}/email`, { email })
        .then(() => {
          this.user.email = email
          this.showEmailModal = false
          this.alertSuccess('Email has been updated')
        })
        .catch((errors) => {
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
    }
  },
  watch: {
    'user.complete_limit': {
      immediate: true,
      handler () {
        this.syncCompleteLimitControls()
      }
    },
    '$store.state.chat.userDetailsInfo.ip_location_check': {
      handler (value) {
        this.user.ip_location_check = value
      }
    },
    '$route.params.userId' () {
      this.$router.go()
    },
    'user.status' (val) {
      this.$emit('update-user-status', val)
      if (this.status) {
        this.$emit('update:status', val)
      }
    },
    'shownAsModal': {
      handler (status) {
        if (status) {
          this.setQuickLinkProperties()
        }
      },
      immediate: true
    },
    'preloadOffersOverview': {
      handler (newVal) {
        // If toggle is enabled (changed to true or was already true) and user details are loaded, trigger prefetch
        // This handles both: prop changing from false to true, and component created with prop already true
        if (newVal === true && this.userId && this.user && this.user.id) {
          // Only prefetch if not already in progress and not already prefetched
          if (!this.$store.getters['user/isPrefetchingOffersOverview'](this.userId)) {
            const prefetchedData = this.$store.getters['user/getPrefetchedOffersOverview'](this.userId)
            // If no valid prefetched data exists, trigger prefetch
            if (!prefetchedData) {
              this.prefetchOffersOverview()
            }
          }
        }
      },
      immediate: false
    }
  },
  created () {
    this.getUserDetails()
    this.getLifetimeReconciliations()
  },
  mounted () {
    this.primeOpinionLogo = $themeConfig.app.appLogoImage
    this.fiveSurveysLogo = $themeConfig.app.fiveSurvesLogoImage
    this.heyCashLogo = $themeConfig.app.heyCashLogoImage
    this.topSurveysLogo = $themeConfig.app.topSurveysLogoImage
    this.topSurveysLogo = $themeConfig.app.topSurveysLogoImage
    this.earnStarLogo = $themeConfig.app.earnStarLogoImage
    this.paidTesterLogo = $themeConfig.app.paidTesterLogoImage

    this.$http
      .get('/api/countries', {
        params: {
          limit: -1
        }
      })
      .then((response) => {
        this.countries = response.data
      })
      .catch((errors) => {
        if (errors && errors.response && errors.response.data) {
          this.alertError(errors.response.data.errors)
        }
      })
  }
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/vue/libs/vue-select.scss";

.inner-spinner {
  position: absolute;
  z-index: 997;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.5);

  ::v-deep {
    span {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50% -50%);
    }
  }
}
</style>
